import type { FishState } from './types';
import { DEPTH_BINS, REEF_ISLANDS, stratumFloorKm2 } from './reef';
import { MAUI_NUI_REEF } from './data/reef/mauiNui';
import { CROWDING_M2_PER_FISH_DAY } from './data/fish/crowding';
import { STEEPNESS_CROWDING } from './data/fish/steepnessCrowding';
import { NENUE_SURVEY } from './data/fish/nenueSurvey';
import { MOANOKEA_SURVEY } from './data/fish/moanokeaSurvey';
import { REEF_SURVEYS } from './data/fish/reefSurveys';
import { civilFromEpochDay, daysInMonth } from './calendar';
import { held } from './held';
import { YEAR_DAYS } from './population';
import { KG_PER_POUND } from './constants';

/**
 * Maui Nui's reef fish (M74, M82): each species' stock on each island,
 * counted by month of age since it settled, its young drawn from the
 * archipelago's larval pool (`GDD.md` section 7). The day's step dies,
 * settles and spawns; its draws are the caller's, so the same step runs the
 * game (binomial and Poisson draws on counts, each island's own stream of the
 * day, M80) and the spin-up that sets its balance (expected values,
 * `scripts/reefFish.ts`). Eight species: three parrotfishes measured on Oʻahu
 * in the same two studies, the bullethead (M74), the palenose (M82) and the
 * redlip (M128); the peacock grouper, the roi, which eats them (M84); the
 * bluespine unicornfish, the kala (M90); and the two jacks, the ulua and the
 * ʻōmilu, which eat them too (M126); and the day octopus, the heʻe mauli,
 * which the ulua eats (M129).
 *
 * **A life** (`FishLife`), measured on Oʻahu from otoliths and gonads: growth
 * by von Bertalanffy's curve, weight by length, the share of the initial
 * phase's females mature and the share of all fish changed to terminal-phase
 * males, each by age, and its oldest age.
 *
 * **Its deaths**: natural deaths at Hamel and Cope's rate for its oldest age
 * (5.40 over it, a year), and fishing on the sizes the fishery takes (Nadon
 * 2017's selectivity) at the rate that gives the fish fully taken NOAA's
 * measured mean length, as Nadon's own method sets its total deaths from
 * that length (`FishSupply.fishingPerYear`), or from the catch, or where a
 * source measures it its own (the kala's, Maui's assessment's, M165); the
 * same on every island but where the sanctuary protects a stock there
 * (`islandFishing`, M165).
 *
 * **Its young**: spawning in its months, its larvae its days afloat (45, the kala's 71, the jacks' 32, the octopus's 30), sent
 * among Maui Nui's four islands and from the rest of the main islands as Wren
 * et al.'s model of reef-fish larvae sends them, each island's larvae as many
 * as its spawners' weight (Nadon 2017's measure of spawning, the weight of
 * the mature fish). How many larvae a kilogram of spawners sends that settle
 * and live, no source served here gives (fecundity and the young's survival
 * are unread, M70, M74 and M82): it is set by the balance, so that the four
 * islands' fish hold NOAA's measured density year on year
 * (`scripts/reefFish.ts`). The rest of the main islands' stock is held at
 * that density until the region's reef stocks are coded.
 */

/**
 * A reef fish stock's recruitment steepness (M214): the share of its unfished recruits it would still get at a fifth of
 * its unfished spawners. No stock-recruit relationship is measured for any of the game's fish (NOAA's assessment of
 * Hawaiʻi's reef fish fits none: "the lack of stock-recruitment relationships meant that we had to rely on a biological
 * reference point ... based on per-recruit spawning stock biomass", Nadon 2017, p. 3, public domain); so Thorson 2020's
 * prediction for a species of the order Perciformes without stock-recruit data, its mode "0.73 (0.22)" (Thorson,
 * *Fish and Fisheries* 21, Table 4, p. 249, "a U.S. Government work and is in the public domain in the USA"), the
 * nearest taxon over the game's fish it prints (its bony fishes' 0.74 (0.23)): their families, the parrotfish, the
 * surgeonfish, the groupers, the jacks, the sea chubs and the goatfish, are of the order as FishBase classed them when
 * Thorson's model was fitted (JUDGED, the classification not read here). NOAA's own in Hawaiʻi, the uku's 0.81 (Nadon
 * 2024, PIFSC-171, Table 1, "Fixed FishLife 2"), is a snapper's from FishLife, whose species figures are FishBase's: a
 * check, not carried.
 */
export const REEF_FISH_STEEPNESS = 0.73;

/** A reef fish's life, each figure with its source. */
export interface FishLife {
  id: 'bullethead' | 'palenose' | 'roi' | 'kala' | 'ulua' | 'omilu' | 'redlip' | 'octopus' | 'nenue' | 'moanokea' | 'manini' | 'taape';
  /** Von Bertalanffy's curve, fork length cm at age t years = L∞ (1 − e^(−k (t − t0))). */
  linfCm: number;
  kPerYear: number;
  t0Years: number;
  /** Round weight g = a L^b, L fork length cm: the initial phase's, or both phases' where one curve is fitted to both. */
  weightA: number;
  weightB: number;
  /** The terminal phase's own weight curve, where it has one. */
  tpWeight?: { a: number; b: number };
  /** The share of initial-phase females mature by age, 1 / (1 + e^(−a (age − b))); an infinite slope is a knife edge, every fish mature from age b. */
  maturitySlope: number;
  maturityAgeYears: number;
  /** The share of all fish terminal-phase males by age, the same curve; none where no curve is measured. */
  sexChangeSlope?: number;
  sexChangeAgeYears?: number;
  /** The share of all fish the sex change curve rises to, where it was fitted to less than all of them (the redlip's 75%, M128); all of them where none is given. */
  sexChangeShare?: number;
  /** Its oldest age, years. */
  maxAgeYears: number;
  /** Natural deaths a year: Hamel and Cope's prior for a fish's oldest age A, a median of 5.40/A (Hamel and Cope 2022, eq. 7; log-space SD 0.31; as Brodziak 2026, PIFSC WP-26-01, p. 2, CC0, restates it). */
  naturalPerYear: number;
  /** The sizes the fishery takes, total length mm at which half and 95% are taken (Nadon 2017's eq. 1). */
  takenHalfTlMm: number;
  takenMostTlMm: number;
  /** The fish fully taken's mean total length in NOAA's diver surveys, mm (Nadon 2017), or the State's commercial landings' where the divers saw too few (the ulua's), the length the balance fits the fishing to (a check where the fishing is the catch's). */
  takenMeanTlMm: number;
  /** The main islands' catch, kg a year, where the fishing is set from it (Nadon 2017): the fishing deaths a year on the sizes taken are the catch over `surveyTakenKg`. */
  catchKgPerYear?: number;
  /**
   * The fishing a year on the sizes taken, where a source measures it and neither the length nor the catch sets it
   * (M165: the kala's, an age-based assessment's), the mean length then a check.
   */
  fishingPerYear?: number;
  /** The main islands' fish of the sizes taken, kg: Nadon 2017's "B from survey", the surveys' fish weighed by the share taken at their size, over the main islands' 96,208 ha of hard bottom to 30 m (his p. 6), the density the balance holds; where the divers' count is none (the jacks', M126), his "B from catch", the catch over his fishing rate. */
  surveyTakenKg: number;
  /** Total length cm from fork length cm: the initial phase's a + b LF, the terminal phase's b LF (and its own a where it has one, the redlip's, M128). */
  ipTlInterceptCm: number;
  ipTlSlope: number;
  tpTlSlope: number;
  tpTlInterceptCm?: number;
  /** Days afloat: the 45 of Wren et al.'s model of reef-fish larvae, "representative for most reef fish" (Wren et al. 2016, p. 3), which the exchange is measured for, or a species' own where measured (the kala's 71, M90; the ʻōmilu's 32, M126), its larvae settling where the 45-day model sends them. */
  larvalDays: number;
  /** Its spawning months, January 1. */
  spawningMonths: readonly number[];
  /**
   * The months from settling its young crowd one another in, each more of
   * its own month's class a square meter of its island's hard bottom each
   * one's chance of dying a day the more (`CROWDING_M2_PER_FISH_DAY`, M84), where its family has a measure; since M214,
   * a fish whose family has none and that neither eats nor is eaten (the kala, the nenue) crowded as its stock's
   * recruitment steepness gives (`steepness`), over the same three months; since M255 (M230's) the parrotfish and the three
   * predators so too; none for the octopus and the moano kea (M255: each held level without, and the octopus held to the
   * steepness only at 70 times its fishing).
   */
  crowdedMonths?: number;
  /**
   * Its stock's Beverton-Holt steepness, the share of its unfished recruits it would still get at a fifth of its unfished
   * spawners (M214), where its young's crowding is not measured: its young's crowding is fitted to it
   * (`STEEPNESS_CROWDING`, `npm run reeffish -- steepness`), each month's settlers crowded by their own number. The
   * parrotfish and their three predators since M255 (M230's), in place of the wrasse's measure M84 gave the parrotfish: the
   * parrotfish's crowding found by the reef's balance at the predation they are eaten by (`steepBalance` in
   * `scripts/reefFish.ts`), the predators' by the steepness's own fit against their tables (`npm run reeffish -- steepness
   * roi ulua omilu`; review M267's: said found by the balance too).
   */
  steepness?: number;
  /** A crowding tried by the steepness's fit (`npm run reeffish -- steepness`), m² a fish a day, in place of its table's; never set in the game. */
  crowdingTried?: number;
  /**
   * An animal without a fish's length (the octopus, M129): its weight kg at an age in days from hatching, a · days^b,
   * where its curve is measured by age, not by length; its length-based figures above are then unused (its fork
   * length, weights by length, total length and the fishery's sizes).
   */
  weightAtAgeDays?: { aKg: number; b: number };
  /** The fishery's least weight, kg: every animal of it and more taken, none less (a size rule set by weight, the octopus's, M129). */
  takenMinKg?: number;
  /** Every animal dead past its oldest age, none carried on in a last class (an animal that breeds once and dies, the octopus, M129). */
  diesAtOldest?: boolean;
  /** Its predators take it at every size, its size's chance 1 (where no measure of the sizes they take is served, the octopus's, M129). */
  takenAtEverySize?: boolean;
  /** Its density in animals a hectare of hard bottom, where it is counted in animals, not weighed at the sizes taken (the octopus's dens, M129): the density the balance holds, every settled animal counted. */
  densityPerHa?: number;
}

/**
 * The bullethead parrotfish, *Chlorurus spilurus* (uhu), each figure with its
 * source as read (M70, M74).
 */
export const BULLETHEAD: FishLife = {
  id: 'bullethead',
  /** L∞ 34.4 cm (SE 1.00), k 0.400 a year (SE 0.045), t0 −0.13 years, 133 fish, all phases (DeMartini et al. 2018, Table 1b). */
  linfCm: 34.4,
  kPerYear: 0.4,
  t0Years: -0.13,
  /** Both phases, 140 fish (DeMartini and Howard 2016, Supporting Table SI). */
  weightA: 0.0199,
  weightB: 3.0464,
  /** a 2.041 (SE 0.419), b 2.8026 years (SE 0.1102) (DeMartini et al. 2018, Table 2a; the share "among all initial phase female fish", Fig. 4). */
  maturitySlope: 2.041,
  maturityAgeYears: 2.8026,
  /** a 0.856 (SE 0.263), b 4.2063 years (SE 0.3562) (ibid., Table 2b; "among all fish", Fig. 4). */
  sexChangeSlope: 0.856,
  sexChangeAgeYears: 4.2063,
  /**
   * "6 and 11 years in Scarus psittacus and Chlorurus spilurus" (ibid.,
   * abstract), by counts of its otoliths' annual bands: the paper validated
   * ages by bomb radiocarbon for "the large species (S. rubroviolaceus and
   * C. perspicillatus)" alone (p. 581; review M82's reader: M70 had this
   * validated).
   */
  maxAgeYears: 11,
  naturalPerYear: 5.4 / 11,
  /** "Estimated." (Nadon 2017, p. 169). */
  takenHalfTlMm: 220,
  takenMostTlMm: 240,
  /**
   * 283 (SD 3, 435 fish), "all length observations (weighted by count) from
   * 2005 to 2016 ... Only lengths above size at full selectivity (LS95) were
   * kept", the main islands' four subregions weighted by their reef (Nadon
   * 2017, pp. 5 and 169). Nadon sets total deaths from it by the growth he
   * took (L∞ 341 mm, K 0.59 a year, oldest 13 years, p. 169), and fishing as
   * those less his natural deaths (0.26 a year; F = Z − M, p. 9); the game
   * sets its fishing from it by its own measured growth and natural deaths
   * (`scripts/reefFish.ts`), every day of the year counted: his eq. 6
   * integrates over every age at once, his stocks "at equilibrium in terms of
   * both mortality rates and recruitment" (p. 15; of this one, "Population
   * abundance has been rising steadily since 2008 while Lbar seemed to be
   * slightly declining", p. 169). Review M74: the game's fish of an age have
   * one length, so its mean swings from 277.5 mm in June to 290.6 in January
   * (MEASURED by month on the balance's year; 276.3 to 291.8 by day); the
   * year's fit (total deaths 0.621 a year) agrees with his eq. 6 solved by the
   * game's growth (0.58 to 0.61, by the initial and the terminal phase's
   * length), where the surveys' months alone, June to August, would fit
   * fishing near 0.04.
   */
  takenMeanTlMm: 283,
  /** 139,438 kg (SD 17,478) (Nadon 2017, p. 169). */
  surveyTakenKg: 139_438,
  /** The initial phase's −0.2609 + 1.0157 LF (96 fish), the terminal phase's 1.0171 LF (44) (DeMartini and Howard 2016, Supporting Table II). */
  ipTlInterceptCm: -0.2609,
  ipTlSlope: 1.0157,
  tpTlSlope: 1.0171,
  larvalDays: 45,
  /**
   * The months its females' gonad index runs above its year's mean, MEASURED,
   * M74, from DeMartini and Howard 2016's Fig. 1 (its yellow curve, by its
   * pixels): above zero from month 3.3 to 8.9, at most in May (0.137); March
   * −0.034, September −0.003. The paper: "from spring through to autumn".
   */
  spawningMonths: [4, 5, 6, 7, 8],
  /**
   * Three months' classes, the nearest to the 90 days the wrasse's crowding
   * was measured over (Shima and Osenberg's six-bar wrasse, a labrid as the
   * parrotfish are; `scripts/data/wrasseCrowding.ts`): the settling month's
   * rest and the two after it, a settler crowded 61 to 91 days, 76.0 on
   * average over its settling season's days, four months' 106.6 (MEASURED,
   * M84, by the calendar). "Density-dependent mortality is
   * largely confined to early juvenile stages in fish populations" (Lorenzen
   * 2022, p. 10).
   */
  crowdedMonths: 3,
  /** Since M255 (M230's) crowded as its steepness gives, at the predation it is eaten by, in place of the wrasse's rate (`STEEPNESS_CROWDING`). */
  steepness: REEF_FISH_STEEPNESS,
};

/**
 * The palenose parrotfish, *Scarus psittacus* (uhu), each figure with its
 * source as read (M82; its reader's quotes checked here against the
 * downloaded text): Oʻahu's fish, the same two studies as the bullethead's.
 */
export const PALENOSE: FishLife = {
  id: 'palenose',
  /** L∞ 32.7 cm (SE 1.96), k 0.486 a year (SE 0.079), t0 −0.01 years (SE 0.10), 101 fish, "Sexes and color phases are pooled within species" (DeMartini et al. 2018, Table 1b and its note; review M82: this had the initial phase's alone, from p. 584's sentence on Fig. 1b's curves by phase). */
  linfCm: 32.7,
  kPerYear: 0.486,
  t0Years: -0.01,
  /** The initial phase's a 0.0180 (SE 0.0015), b 3.0641 (SE 0.0258), 91 fish; the terminal phase's a 0.0214 (SE 0.0105), b 3.0190 (SE 0.1461), 34 fish (DeMartini and Howard 2016, Supporting Table SI). */
  weightA: 0.018,
  weightB: 3.0641,
  tpWeight: { a: 0.0214, b: 3.019 },
  /** a 5.517 (SE 0.770), b 2.0445 years (SE 0.0352), 7 age classes (DeMartini et al. 2018, Table 2a). */
  maturitySlope: 5.517,
  maturityAgeYears: 2.0445,
  /** a 1.737 (SE 0.797), b 2.8091 years (SE 0.2805), 7 age classes (ibid., Table 2b). */
  sexChangeSlope: 1.737,
  sexChangeAgeYears: 2.8091,
  /** "Observed maximum ages were 6 and 11 years in S. psittacus and C. spilurus" (ibid., p. 584), by band counts, not validated (p. 581). */
  maxAgeYears: 6,
  naturalPerYear: 5.4 / 6,
  /** "Estimated" (Nadon 2017, p. 181): "Selectivity had to be estimated given the lack of catch data". */
  takenHalfTlMm: 200,
  takenMostTlMm: 210,
  /** "L diver survey 254 2 mm 344 NOAA-CREP diver survey" (Nadon 2017, p. 181), on the same rule as the bullethead's (pp. 5 and 9). */
  takenMeanTlMm: 254,
  /** 130,295 kg (SD 41,926): "The population biomass estimate should be reasonably accurate given that this species extends only slightly beyond the maximum diver survey depth (30 m)" (Nadon 2017, p. 181). */
  surveyTakenKg: 130_295,
  /** The initial phase's −0.5880 + 1.0634 LF (91 fish), the terminal phase's 1.0911 LF (34) (DeMartini and Howard 2016, Supporting Table II). */
  ipTlInterceptCm: -0.588,
  ipTlSlope: 1.0634,
  tpTlSlope: 1.0911,
  /** Wren et al.'s, as the bullethead's: no time afloat measured for it in Hawaii in the sources read (M82). */
  larvalDays: 45,
  /**
   * The months its females' gonad index runs above its year's mean, the
   * bullethead's rule: MEASURED, M82, from DeMartini and Howard 2016's Fig. 1
   * (its black curve, by its pixels, the method giving the bullethead's yellow
   * curve above zero from month 3.32 to 8.99, at most 0.139 in May, M74's
   * 3.3, 8.9 and 0.137): above zero from month 10.00 to 11.85, at most in
   * November (0.170); October 0.001, December −0.033, February −0.706 (the
   * curve starts in February). The paper, of the three small species
   * together: "spawning by the three small-bodied species extended from
   * spring through to autumn" (p. 10).
   */
  spawningMonths: [10, 11],
  /** As the bullethead's (M84): 59 to 89 days, 74.8 on average over its settling season's days, four months' 104.8 (MEASURED, by the calendar). */
  crowdedMonths: 3,
  /** Since M255 (M230's) crowded as its steepness gives, at the predation it is eaten by, in place of the wrasse's rate (`STEEPNESS_CROWDING`). */
  steepness: REEF_FISH_STEEPNESS,
};

/**
 * The peacock grouper, *Cephalopholis argus* (roi), brought to Hawaii from
 * Tahiti in 1956 (Nadon 2017, p. 193), Maui Nui's first predator (M84; it
 * eats the parrotfish, `predation.ts`). Each figure with its source as read
 * (M84's readers' quotes checked here against the downloaded text). Its own
 * studies in Hawaii, Donovan et al. 2013 (growth, oldest age) and Schemmel
 * et al. 2016 (maturity, sex change, spawning), were refused at every host
 * (a bot check, the proxy); the user may hand them over. Until then its
 * growth, oldest age and maturity are Nadon's compilation of them.
 */
export const ROI: FishLife = {
  id: 'roi',
  /**
   * L∞ 506 mm (SD 11), K 0.075 a year (SD 0.007), a0 −6.5 years, 590 fish,
   * "Mean: Donovan (2013), SD: Kritzer (2001)" (Nadon 2017, p. 193): compiled.
   * Its lengths are total lengths, which in the roi are fork lengths
   * ("equal to fork length in C. argus due to their rounded caudal fin
   * shape", Dierking, Williams and Walsh 2009, *Fish. Bull.* 107, p. 465).
   * Nadon: "the growth curve did not have juvenile age estimates which
   * explains the highly negative a0 and the resulting low K value": by it a
   * roi settles 19.8 cm long.
   */
  linfCm: 50.6,
  kPerYear: 0.075,
  t0Years: -6.5,
  /** M (g) = a TL^b, TL cm: a 0.0125, b 3.122, 110 fish of Oʻahu, r² 0.98 (Dierking, Williams and Walsh 2009, Table 1). */
  weightA: 0.0125,
  weightB: 3.122,
  /**
   * "Lmat 268 8 mm 100? Mean: Myers (1999)" (Nadon 2017, p. 193), "a less
   * reliable source", taken as Nadon takes it, a knife edge ("age where 50%
   * of individuals are mature, with knife-edge assumption", p. 10): every
   * roi mature from 268 mm, 3.56 years by the growth above.
   */
  maturitySlope: Infinity,
  maturityAgeYears: -6.5 - Math.log(1 - 26.8 / 50.6) / 0.075,
  /** "Longevity 25 1.4 yr 590 Mean: Donovan (2013)" (Nadon 2017, p. 193): "we always selected the oldest recorded age" (p. 10). */
  maxAgeYears: 25,
  naturalPerYear: 5.4 / 25,
  /** "LS50 270 ... LS95 310 ... mm ... DAR commercial data" (Nadon 2017, p. 193): fitted to the State's commercial sizes (p. 4). */
  takenHalfTlMm: 270,
  takenMostTlMm: 310,
  /**
   * "L diver survey 373 4 mm 614 NOAA-CREP diver survey" (Nadon 2017, p.
   * 193). A check, not the fishing's fit: by the game's natural deaths
   * (Hamel and Cope's 5.40 over 25 years, 0.216 a year) the fish fully
   * taken average less than this with no fishing at all, where Nadon's
   * own M, 0.13 (−ln 0.04 over 25, p. 9), leaves him 0.02 a year of
   * fishing; the fishing is the catch's (`catchKgPerYear`).
   */
  takenMeanTlMm: 373,
  /** "B from survey 777,397 56,961 kg" (Nadon 2017, p. 193), "likely biased downward given that this species' range extends beyond diver depths, to 80 m". */
  surveyTakenKg: 777_397,
  /** "Total catch 4,552 5,594 kg" (Nadon 2017, p. 193), commercial and recreational: "the catch estimate seemed low" (ibid.). */
  catchKgPerYear: 4_552,
  ipTlInterceptCm: 0,
  ipTlSlope: 1,
  tpTlSlope: 1,
  /** Wren et al.'s 45, as the parrotfish's; its own "estimated to be ~40 days (Schemmel et al., 2016)" (Amirthalingam et al. 2025, *Front. Mar. Sci.* 12: 1665295, p. 3), read at second hand. */
  larvalDays: 45,
  /** "Ripe gonads have been observed in May-early June, and the possibility of a second spawning each year has not been evaluated" (The Nature Conservancy 2013, *South Kohala Conservation Action Plan Roi Research Report*, for Hawaii's DAR, p. 39): an unsourced field note. */
  spawningMonths: [5, 6],
  /**
   * Its young crowded over the parrotfish's three months, as its steepness gives (M255, M230's: no crowding of its young
   * is measured; Thorson 2020's Perciformes mode, `REEF_FISH_STEEPNESS`, as the parrotfish it eats are given).
   */
  crowdedMonths: 3,
  steepness: REEF_FISH_STEEPNESS,
};

/**
 * The bluespine unicornfish, *Naso unicornis*, the kala (M90): a surgeonfish
 * that browses the reef's leafy brown algae (Dictyota and Sargassum in its
 * stomachs at La Perouse Bay, Maui; Jones 1967, Table III), the surgeonfish
 * with the most of the game's figures measured in Hawaii (M90's readers:
 * validated growth and age, its larvae's days afloat, its spawning and sexes
 * on Maui, NOAA's density). Not named in the roi's stomachs (Dierking,
 * Williams and Walsh 2009, Table 2), so the game does not feed it to the roi,
 * though seven of the fourteen surgeonfish there were not named and a kala's
 * first year is of the sizes the roi takes (M90, open); its young's crowding
 * unmeasured for its family where a plain request is served (M90, open).
 */
export const KALA: FishLife = {
  id: 'kala',
  /**
   * The pooled three-parameter curve: L∞ 47.8 cm fork length (SE 0.3), k
   * 0.44 a year (0.02), t0 −0.12 years (0.04), 534 fish of Oʻahu, their ages
   * validated by bomb radiocarbon (Andrews et al. 2016, *Can. J. Fish. Aquat.
   * Sci.* 73, Table 2A): measured. "Growth curves differed overall between
   * males and females" (p. 1581), by little (L∞ 48.0 and 47.6). Nadon 2017
   * (p. 69, public domain) gives the same k and t0 from it ("K 0.44 0.02 yr-1
   * 534 Mean and SD: Andrews et al. (2016)", "a0 -0.12"), its L∞ 480 mm.
   */
  linfCm: 47.8,
  kPerYear: 0.44,
  t0Years: -0.12,
  /**
   * W = 0.0280 FL^2.91, W in g and FL in cm, 8,447 kala weighed and measured on Guam's market by NOAA's biosampling, 17.9
   * to 57.2 cm (Matthews, Schemmel and Cruz 2024, NOAA PIFSC Admin. Rep. H-24-03, Table 1, p. 5; "L is the fork length
   * (cm)", p. 3; public domain, an informal report): the largest sample measured on fork length, the curve the game
   * applies it to (M183). None fitted to Hawaiian kala was read (Andrews et al. 2016 weighed 534 Oʻahu fish, no curve
   * given). Checks: American Samoa's 0.0329 FL^2.85 and the Marianas' 0.0271 FL^2.91 (H-19-03, H-24-06, the same
   * program), 7% and 3% lighter at 49 cm; Maui's (Angst 2026, p. 42: "a = 0.003292, b = 2.179", its units unstated,
   * mm and g by its weights) 3% heavier there. It replaces Kulbicki 2005's 1.65e-5 L^3.035 (L mm, compiled by Nadon
   * 2017, p. 69), NOAA's surveys' curve, 4% heavier at 49 cm.
   */
  weightA: 0.028,
  weightB: 2.91,
  /**
   * "Lmat 355 10 mm 295 Mean and SD: Eble (2009)" (Nadon 2017, p. 69):
   * compiled, the females' "35.5 cm FL" (DeMartini et al. 2014, as Andrews
   * et al. 2016 quote it, p. 1583), taken as Nadon takes maturity, a knife
   * edge (p. 10): every kala mature from 35.5 cm, 2.97 years by the growth
   * above ("near 3 years for females", Andrews et al. 2016, p. 1584).
   */
  maturitySlope: Infinity,
  maturityAgeYears: -0.12 - Math.log(1 - 35.5 / 47.8) / 0.44,
  // No sex change: "Histological analysis confirmed that Kala are gonochoristic" (Angst 2026, Maui, p. 19).
  /**
   * The age readings "refined by the experienced age-reader to span 0 to 50
   * years overall" (Andrews et al. 2016, p. 1580), Nadon's longevity (2017,
   * p. 69); one fish read at 48 was 54.5 ± 3.0 years by its radiocarbon
   * (Andrews et al. 2016, p. 1579).
   */
  maxAgeYears: 50,
  naturalPerYear: 5.4 / 50,
  /** "LS50 230 ... LS95 260 ... mm ... Estimated from HMRFS data and other surgeonfishes." (Nadon 2017, p. 69): estimated. */
  takenHalfTlMm: 230,
  takenMostTlMm: 260,
  /**
   * "L diver survey 348 5 mm 522 NOAA-CREP diver survey" (Nadon 2017, p.
   * 69), the length the fishing is fitted to, as the parrotfish's. Nadon's
   * F from it, 0.60 a year, "could not be explained by the relatively modest
   * catch estimate", 20,197 kg a year (ibid.), whose commercial part counts
   * three *Naso* together "as reference only": a check (M90, open).
   */
  takenMeanTlMm: 348,
  /**
   * Its fishing from Maui's own fishery since M165, not fitted to the length above: an age-structured assessment of 578
   * kala from Maui's small-boat purse-seine fishery, May 2024 to June 2025, gives F = 0.133 a year at M = 0.104 (Angst
   * 2026, MS thesis, University of Hawaiʻi at Hilo, p. 41; its length-based estimate 0.158, 95% CI 0.086 to 0.208, pp.
   * 41-42), where Nadon's length-based 0.60 rests on a natural death rate its author doubts ("This high longevity leads to
   * an extremely low M estimate, which in turn leads to a high F estimate", Nadon 2017, p. 69) and the State's fisheries
   * division finds "MHI kala stocks may not be depleted" (BLNR item F-5, 7 December 2023, p. 7). Maui's taken for Maui
   * Nui's four islands, fished alike (M165: the islands' fish about as dense as the main islands', 32, 36 and 31 g m⁻² of
   * all fishes on Maui, Molokaʻi and Lānaʻi against 29.8, McCoy et al. 2017, PIFSC DR-17-001, Fig. 6, read off the
   * figure). Its fishery takes fish half selected at 501.82 mm fork length (Angst, p. 41), the game's fishery from
   * Nadon's 230 mm: the two not the same fish (open). A thesis, not yet reviewed; its facts cited, the text not carried.
   */
  fishingPerYear: 0.133,
  /** "B from survey 364,114 74,939 kg" (Nadon 2017, p. 69), "likely biased downward given that this species has been recorded to 120-m depths and diver surveys only reach 30-m depths" (ibid.). */
  surveyTakenKg: 364_114,
  /**
   * Total length the fork length (ESTIMATED, M183): no measured relation the game may carry was found. NOAA's surveys'
   * factor, 0.96 (NCEI ERDDAP `CRCP_Reef_Fish_Surveys_Hawaii`), is FishBase's ("LW and LL conversion parameters were
   * taken from FishBase", McCoy et al. 2017, PIFSC DR-17-001, p. 16), its own source unnamed, so it is not carried. The
   * one Hawaiian study measuring both on the same fish, Eble, Langston and Bowen 2009 (a final report to DAR), refused
   * every copy (asked of the user). A grown kala's tail carries filaments beyond its lobes (DeMartini 2016,
   * *Copeia* 104: 498, p. 500: the filaments 15.4% of a female's fork length and 18.3% of a male's, Table 1B), so
   * whether a diver's total length takes them in is unsaid (NOAA's divers measure "to the tip of the longer lobe of the
   * caudal fin", Ayotte et al. 2015, p. 9): open.
   */
  ipTlInterceptCm: 0,
  ipTlSlope: 1,
  tpTlSlope: 1,
  /**
   * "Overall median length-at-settlement was 5.9 cm at a median PLD of 71
   * days" (Andrews et al. 2016, p. 1580, daily rings of Oʻahu's young of the
   * year): measured. Its larvae settle where Wren et al.'s 45-day model sends
   * a reef fish's: "connectivity studies using a 45 day PLD can be
   * representative of fish species with a longer PLD in Hawai'i" (Wren 2016,
   * dissertation, p. 21).
   */
  larvalDays: 71,
  /** "Kala spawning activity peaked during April-May" (Angst 2026, p. 17: monthly gonad index of Maui's kala, seven months sampled). */
  spawningMonths: [4, 5],
  /** Its young crowded over the parrotfish's three months, as its steepness gives (M214). */
  crowdedMonths: 3,
  steepness: REEF_FISH_STEEPNESS,
};

/**
 * The jacks (M126), Maui Nui's native reef predators: the giant trevally,
 * *Caranx ignobilis*, and the bluefin trevally, *C. melampygus* ("Giant
 * Trevally [known in Hawai’i as ulua aukea] Caranx ignobilis and Bluefin
 * Trevally [known in Hawai’i as ‘ōmilu] Caranx melampygus", Pardee, Wiley and
 * Fendrick 2023, *Mar. Coast. Fish.* 15: e10264, p. 1, CC BY). Their
 * lives are the main islands' (Pardee, Wiley and Springer 2021, *J. Fish
 * Biol.* 99: 1247, read as NOAA's copy of the accepted manuscript: 180 ulua
 * and 194 ʻōmilu of Oʻahu, Maui and Hawaiʻi aged by their otoliths' yearly
 * bands, "previously validated using bomb 14C dating of C. ignobilis
 * samples", p. 6), not the Northwestern islands' that Nadon 2017 compiles
 * (Sudekum et al. 1991: few fish, aged by their otoliths' daily rings, the
 * rings' daily laying checked on juveniles in tanks; their L∞ "more than 900
 * mm and 240 mm greater", Pardee et al. 2021, p. 9). The divers' counts serve neither: of the ulua "There
 * were insufficient diver observations in the MHI to generate a reliable Lbar
 * estimate" (Nadon 2017, p. 81), and of the ʻōmilu "the fast swimming
 * behavior of this species made estimating population size unreliable" (p.
 * 88); so their density is Nadon's from the catch (`surveyTakenKg`). They eat
 * the parrotfish (`predation.ts`).
 */
export const ULUA: FishLife = {
  id: 'ulua',
  /** "Combined sex VBGF values ... L∞= 1064 mm and K = 0.18 year-1" (Pardee et al. 2021, p. 8), t0 −0.22 (its Table 1), fork length: measured. */
  linfCm: 106.4,
  kPerYear: 0.18,
  t0Years: -0.22,
  /** W g = 1.34 × 10⁻⁵ FL mm^3.06, r² 0.98 (Pardee et al. 2021, p. 7 and Table 1), here per cm of fork length. */
  weightA: 1.34e-5 * 10 ** 3.06,
  weightB: 3.06,
  /** 50% mature at 3.8 years, both sexes (Pardee et al. 2021, Table 1; 4.4 the females', 2.8 the males'), a knife edge (no slope printed), as Nadon takes maturity. */
  maturitySlope: Infinity,
  maturityAgeYears: 3.8,
  /** "Maximum observed ages for C. ignobilis and C. melampygus were 31 years and 24 years, respectively" (Pardee et al. 2021, p. 1). */
  maxAgeYears: 31,
  naturalPerYear: 5.4 / 31,
  /** "LS50 350 ... LS95 430 ... mm ... DAR commercial data" (Nadon 2017, p. 81): fitted to the State's commercial sizes. */
  takenHalfTlMm: 350,
  takenMostTlMm: 430,
  /** "L commercial 761 6 mm 5372 DAR commercial data" (Nadon 2017, p. 81), no diver mean: a check, the fishing the catch's. */
  takenMeanTlMm: 761,
  /**
   * "B from catch 1,070,000 3,930,000 kg" (Nadon 2017, p. 81), the catch over his fishing rate ("dividing total catch
   * by an estimate of current F", p. 14), its SD near four times it: derived. His catch-derived biomasses were "Almost
   * all ... lower than those derived from diver surveys" (ibid.), where both were. Assumed (M126's reviewer): the whole
   * fished stock, to its deepest (228 m, Nadon's Table 5) and in federal waters too, placed on the 0-30 m hard bottom
   * the game's reef is, so an upper bound for the reef's jacks. The towed divers' count of the roving piscivores
   * (Weijerman et al. 2013: Oʻahu's apex predators 0.26 g m⁻² "from survey data", Table 3, p. 7, its sharks' and roving
   * piscivores' 0.04 and 0.48 t km⁻² in their model's Table S5 halved, the model having had to "double the biomass to
   * mass balance the model", text S1; Kona's 0.2, Table S4) gives Oʻahu's ulua about a seventh of it (69.4% of 0.24) and
   * Kona's ʻōmilu about an eleventh (38.3% of 0.2): a lower bound, Oʻahu's catch of them, 0.44 t km⁻² a year (text S1),
   * more than 0.24 yields at the model's production of once its biomass a year (towed divers' counts "believed to be
   * more accurate" than the small-scale surveys', p. 9; of the ulua, "insufficient diver observations", Nadon, p. 81).
   * Open (M126).
   */
  surveyTakenKg: 1_070_000,
  /** "Total catch 142,429 113,530 kg" (Nadon 2017, p. 81), "dominated by the recreational sector" (ibid.). */
  catchKgPerYear: 142_429,
  /** TL = 1.070 FL + 35.7 mm, 107 fish (Sudekum et al. 1991, *Fish. Bull.* 89, Table 1, p. 495). */
  ipTlInterceptCm: 3.57,
  ipTlSlope: 1.07,
  tpTlSlope: 1.07,
  /** The ʻōmilu's 32 days (below): none read for the ulua; the nearest measured, its congener's (assumed). */
  larvalDays: 32,
  /** "Both species had a spawning season between February and September, with peak spawning occurring from May to July" (Pardee et al. 2023, p. 1; the gonads of 116 mature female ulua and 61 ʻōmilu of Oʻahu, Maui, Molokaʻi and Hawaiʻi, 2018 to 2022). */
  spawningMonths: [2, 3, 4, 5, 6, 7, 8, 9],
  /**
   * Its young crowded over the parrotfish's three months, as its steepness gives (M255, M230's: no crowding of its young
   * is measured; Thorson 2020's Perciformes mode, `REEF_FISH_STEEPNESS`, as the parrotfish it eats are given).
   */
  crowdedMonths: 3,
  steepness: REEF_FISH_STEEPNESS,
};

/** The bluefin trevally, *Caranx melampygus*, the ʻōmilu (M126, above). */
export const OMILU: FishLife = {
  id: 'omilu',
  /** "L∞= 718 mm and K = 0.20 year-1" (Pardee et al. 2021, p. 8), t0 −0.29 (its Table 1), fork length: measured. */
  linfCm: 71.8,
  kPerYear: 0.2,
  t0Years: -0.29,
  /** W g = 6.77 × 10⁻⁶ FL mm^3.15, r² 0.95 (Pardee et al. 2021, p. 8 and Table 1), here per cm of fork length. */
  weightA: 6.77e-6 * 10 ** 3.15,
  weightB: 3.15,
  /** 50% mature at 3.6 years, both sexes (Pardee et al. 2021, Table 1; 4.1 the females', 2.9 the males'), a knife edge. */
  maturitySlope: Infinity,
  maturityAgeYears: 3.6,
  /** 24 years, the oldest aged (Pardee et al. 2021, p. 1). */
  maxAgeYears: 24,
  naturalPerYear: 5.4 / 24,
  /** "LS50 325 ... LS95 370 ... mm ... DAR commercial data" (Nadon 2017, p. 88). */
  takenHalfTlMm: 325,
  takenMostTlMm: 370,
  /** "L diver survey 465 9 mm 169 NOAA-CREP diver survey" (Nadon 2017, p. 88): a check, the fishing the catch's. */
  takenMeanTlMm: 465,
  /** "B from catch 811,000 3,500,000 kg" (Nadon 2017, p. 88), as the ulua's: derived, an upper bound for the reef's (the ulua's, above). */
  surveyTakenKg: 811_000,
  /** "Total catch 148,127 50,035 kg" (Nadon 2017, p. 88). */
  catchKgPerYear: 148_127,
  /** TL = 1.052 FL + 24.3 mm, 140 fish (Sudekum et al. 1991, Table 1, p. 495). */
  ipTlInterceptCm: 2.43,
  ipTlSlope: 1.052,
  tpTlSlope: 1.052,
  /** "Based on daily ring counts of seven C. melampygus specimens ranging from 75 mm to 221mm settlement occurred around 32 days (SD ± 5 days)" (Pardee et al. 2021, p. 8): measured. */
  larvalDays: 32,
  /** As the ulua's (Pardee et al. 2023, p. 1). */
  spawningMonths: [2, 3, 4, 5, 6, 7, 8, 9],
  /**
   * Its young crowded over the parrotfish's three months, as its steepness gives (M255, M230's: no crowding of its young
   * is measured; Thorson 2020's Perciformes mode, `REEF_FISH_STEEPNESS`, as the parrotfish it eats are given).
   */
  crowdedMonths: 3,
  steepness: REEF_FISH_STEEPNESS,
};

/**
 * The redlip parrotfish, *Scarus rubroviolaceus* (uhu ʻeleʻele), the main
 * islands' largest parrotfish stock by weight (M128): Oʻahu's fish, the same
 * two studies as the bullethead's and the palenose's, its ages validated by
 * bomb radiocarbon.
 */
export const REDLIP: FishLife = {
  id: 'redlip',
  /**
   * L∞ 53.5 cm (SE 0.80), k 0.410 a year (SE 0.032), t0 +0.12 years (SE 0.15), 272 fish, sexes and phases pooled
   * (DeMartini et al. 2018, Table 1b, p. 585), the curve the bullethead's and the palenose's are from. Its t0 past
   * zero puts a settler, 45 days from hatching, at 0.07 cm (the fit's, below the sizes aged; the curve through zero,
   * Table 1c's 53.8 and 0.389, would put it at 2.5).
   */
  linfCm: 53.5,
  kPerYear: 0.41,
  t0Years: 0.12,
  /** The initial phase's a 0.0298 (SE 0.0033), b 2.9065 (SE 0.0289), 413 fish; the terminal phase's a 0.0204 (SE 0.0054), b 3.0156 (SE 0.0683), 111 fish (DeMartini and Howard 2016, Supporting Table I). */
  weightA: 0.0298,
  weightB: 2.9065,
  tpWeight: { a: 0.0204, b: 3.0156 },
  /** a 1.457 (SE 0.108), b 3.2592 years (SE 0.0464), 17 age classes (DeMartini et al. 2018, Table 2a, p. 585). */
  maturitySlope: 1.457,
  maturityAgeYears: 3.2592,
  /**
   * a 0.809 (SE 0.109), b 5.1341 years (SE 0.1615), 6 age classes (ibid., Table 2b), its curve rising to 75% of the
   * fish, not all: "the value 75 was used as the asymptotic percentage value in the numerator instead of 100 for SRUB
   * to avoid the influence of old initial phase females" (ibid., its note).
   */
  sexChangeSlope: 0.809,
  sexChangeAgeYears: 5.1341,
  sexChangeShare: 0.75,
  /** "Scarus rubroviolaceus was aged to 19 years (specimen SR-16)" (ibid., Fig. 2, p. 584), validated by bomb radiocarbon; Nadon 2017's 22 (Howard 2008, p. 187), not read. */
  maxAgeYears: 19,
  naturalPerYear: 5.4 / 19,
  /** "Estimated." (Nadon 2017, p. 187): "selectivity had to be estimated given the lack of catch data". */
  takenHalfTlMm: 240,
  takenMostTlMm: 260,
  /** "L diver survey 401 8 mm 1054 NOAA-CREP diver survey" (Nadon 2017, p. 187), on the same rule as the bullethead's (pp. 5 and 9); no catch, the fishing fitted to it. */
  takenMeanTlMm: 401,
  /** 623,532 kg (SD 80,385): "may be biased downward given that this species' range extends to 68 m depth, which is beyond the depth of the diver surveys (30 m)" (Nadon 2017, p. 187). */
  surveyTakenKg: 623_532,
  /**
   * The initial phase's −2.8037 + 1.1433 LF (420 fish), the terminal phase's −11.1666 + 1.3995 LF (113) (DeMartini and
   * Howard 2016, Supporting Table II), lines fitted to grown fish: below 19.6 cm of fork length the initial phase's
   * gives a total length shorter than the fork's, and below 2.45 cm none (a settler's 0.07 cm, above, is −2.7 cm by
   * it). The predators read the young's lengths from 5 cm (`PREY_SIZE`): each class's total length is held at its fork
   * length or more (`ageClass`, M128's review).
   */
  ipTlInterceptCm: -2.8037,
  ipTlSlope: 1.1433,
  tpTlSlope: 1.3995,
  tpTlInterceptCm: -11.1666,
  /** Wren et al.'s, as the bullethead's: no time afloat measured for it in Hawaii in the sources read (M128). */
  larvalDays: 45,
  /**
   * The months its females' gonad index runs above its year's mean, the bullethead's rule: MEASURED, M128, from
   * DeMartini and Howard 2016's Fig. 1 (its green curve, by its pixels, the method giving the bullethead's yellow
   * curve above zero from month 3.32 to 8.99, at most 0.139 in May, as M82 measured it): above zero from month 1.38 to
   * 4.87, at most 1.078 at month 3.79, and from 5.89 to 6.58 (0.046 at its top); January −0.157, May −0.106, July
   * −0.105. The paper: "largely restricted to spring to early summer and peaked in March to April", with "A second
   * spawning peak in September to November" indicated (p. 10), below the year's mean on the curve.
   */
  spawningMonths: [2, 3, 4, 6],
  /** As the bullethead's (M84), the family's measure: 61 to 91 days, 75.9 on average over its settling season's days, four months' 106.5 (MEASURED, M128, by the calendar, the count giving the bullethead's 61 to 91, 76.0 and 106.6). */
  crowdedMonths: 3,
  /** Since M255 (M230's) crowded as its steepness gives, at the predation it is eaten by, in place of the wrasse's rate (`STEEPNESS_CROWDING`). */
  steepness: REEF_FISH_STEEPNESS,
};

/**
 * The day octopus, *Octopus cyanea* (heʻe mauli), M129: an animal that lives about a year, breeds once and dies, its
 * weight measured by age (not by length), its density counted from its dens. Its life as measured at Ningaloo, Western
 * Australia, where 102 were aged by their stylets' daily rings (Herwig et al. 2012, *PLoS ONE* 7: e43679, CC BY); its
 * Hawaiian life is compiled from Van Heukelem's work, which is unread (Kramer 1986, in NOAA TR NMFS 38, pp. 74-75, a
 * US government work; Van Heukelem 1976's dissertation behind a bot check, M129).
 */
export const OCTOPUS: FishLife = {
  id: 'octopus',
  /** Unused: its weight is by age (`weightAtAgeDays`), its fishery by weight (`takenMinKg`). */
  linfCm: 0,
  kPerYear: 0,
  t0Years: 0,
  weightA: 0,
  weightB: 0,
  ipTlInterceptCm: 0,
  ipTlSlope: 0,
  tpTlSlope: 0,
  takenHalfTlMm: 0,
  takenMostTlMm: 0,
  takenMeanTlMm: 0,
  /**
   * W kg = 2 × 10⁻⁵ · days^2.042, days from hatching (Herwig et al. 2012, Fig. 1, p. 3: "y = 2E-05x^2.042, R² = 0.591",
   * 102 octopus of 0.017 to 1.91 kg aged 48 to 314 days by their stylets): fitted. Its Table 1 gives the coefficient as
   * 0.001, which does not reproduce its points (2.29 kg at 300 days by the figure's, MEASURED, M129). Hawaiʻi's, compiled:
   * "W = 1.28 × 10⁻⁶ X^3.92", X days (Kramer 1986, p. 74), which gives 14 kg at a year against Hawaiʻi's largest, 6.5 kg
   * (as Herwig et al. compile it, p. 4), so holds only over the young ages it was fitted to. Herwig et al.'s own is
   * carried past its oldest, 314 days, to the game's 410 (extrapolated): 3.4 kg at a year and 4.3 at 410 days, against
   * their largest 1.91 kg and within Hawaiʻi's 6.5 (MEASURED, M129's review).
   */
  weightAtAgeDays: { aKg: 2e-5, b: 2.042 },
  /** Females "matured at around 225 days" (Herwig et al. 2012, p. 3, histology, its logistic's half), taken as a knife edge, in the game's 365-day year: measured. Hawaiʻi's, compiled: "Females reach sexual maturity 10-13 months after settling" (Kramer 1986, p. 74). */
  maturitySlope: Infinity,
  maturityAgeYears: 225 / YEAR_DAYS,
  /**
   * 13 months, every octopus dead past it (`diesAtOldest`): "maximum ages for Hawaiian O. cyanea are 400 d" (Herwig et
   * al. 2012, p. 4, compiled), the month it falls in; "For both sexes, the average natural lifespan appears to be 12-15
   * months" and "Females die following egg brooding and hatching" (Kramer 1986, p. 74, compiled). Ningaloo's oldest, 314
   * days (Herwig et al.).
   */
  maxAgeYears: 13 / 12,
  diesAtOldest: true,
  /** Hamel and Cope's prior for its oldest age, 5.40/A (as every reef stock's), a fish's measure taken for an octopus (assumed). Herwig et al.'s "2.9%" a day (p. 3), from Hoenig's regression, comes from its oldest age in days, not years (MEASURED, M129: in years the same regression gives 3.88 a year). */
  naturalPerYear: 5.4 / (13 / 12),
  /** "unlawful for any person to take, possess, or sell any he‘e less than one pound in weight" (Hawaiʻi Administrative Rules §13-95-55): 0.4536 kg. */
  takenMinKg: KG_PER_POUND,
  /** "During 1961-79, the annual landings varied between 1,722 and 10,988 kg and averaged 4,611 kg" (Kramer 1986, p. 75), the State's commercial landings: the least it takes, as "recreational harvest probably exceeds reported commercial landings" (ibid.). */
  catchKgPerYear: 4_611,
  /** Unused: its density is in animals (`densityPerHa`), not in the weight of the sizes taken. */
  surveyTakenKg: 0,
  /**
   * Its dens a hectare: "The average density of active dens throughout Kaneohe Bay was 0.12 per 100 m² of search area"
   * (Sims 1998, Kāneʻohe Bay, Oʻahu, p. 10, its own average, the method not given; review M149: its pooled 30 active dens
   * in "approximately 26,800" m², July and August, p. 11, give 0.112, so 0.12 is likely its sites' mean), one octopus a den
   * ("an O. cyanea occupies one den for up to four weeks", ibid., p. 6, from the behavior studies it cites): 12 a
   * hectare, measured. A lower bound by its own word: "obtained during what is regarded as a season of low density"
   * (p. 11), its search on the reef flats alone.
   */
  densityPerHa: 0.12 * 100,
  /** "close to the centre of the stylet at around 30 d (+/−7.3 sd, n = 5)", the mark of its settling (Herwig et al. 2012, p. 3): measured on five, its reading the authors'. Hawaiʻi's: "pelagic for an undetermined time before settlement" (Kramer 1986, p. 74). */
  larvalDays: 30,
  /** "Hee appears to spawn throughout the year" (Kramer 1986, p. 74, from Van Heukelem 1973): every month. */
  spawningMonths: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
  /** No measure of the sizes its predators take (the ulua's stomachs name it, not its sizes, Sudekum et al. 1991, Table 3). */
  takenAtEverySize: true,
  // No crowding: none measured for its young (its settlers' dens, Sims 1998, are counted, not their crowding; M129, open).
};


/** The main islands' hard bottom to 30 m, ha, NOAA's density's area: "hard-bottom population area (96,208 ha)" (Nadon 2017, p. 6). */
export const MAIN_ISLANDS_HARD_BOTTOM_HA = 96_208;
/** Ogino et al. 2020's females' growth and oldest female (below). */
const NENUE_GROWTH = { linfMm: 512, k: 0.26, t0: -0.46 } as const;
const NENUE_OLDEST_YEARS = 46;
/** Total length mm from fork length mm, Oʻahu's chubs' (below). */
const nenueTlMm = (forkMm: number): number => NENUE_SURVEY.tlInterceptMm + NENUE_SURVEY.tlSlope * forkMm;
/** The age at a fork length mm by the growth below. */
const nenueAgeAt = (forkMm: number): number => NENUE_GROWTH.t0 - Math.log(1 - forkMm / NENUE_GROWTH.linfMm) / NENUE_GROWTH.k;

/**
 * The nenue, Hawaiʻi's sea chubs (*Kyphosus spp.*), each figure with its source as read (M178). NOAA's divers name Maui Nui's
 * chubs to the genus alone (183 of 188 rows, M178's reader), so the stock is the genus's. Its lengths, weights and
 * maturity are Oʻahu's chubs', the five species pooled as the stock is (Weng 2025, NCEI Accession 0303929, CC0,
 * `scripts/nenue.ts`); no growth, age or larval life of a Hawaiian chub was found, and its growth and oldest age are
 * *Kyphosus bigibbus*'s off Nagasaki, Japan, a temperate congener's (Ogino, Furumitsu, Kiriyama and Yamaguchi 2020,
 * *Mar. Freshw. Res.* 71: 855, doi:10.1071/MF19231, handed over by the user; CC BY-NC-ND, its facts cited): open, the
 * nearest measured.
 */
export const NENUE: FishLife = {
  id: 'nenue',
  /**
   * The females' curve, "Lt = 512[1 − e^(−0.26(t+0.46))]" fork length mm (p. G, Fig. 7; 190 females, ages read from
   * otolith sections, their opaque zones "validated as annuli", APE 0.2%): measured. The males' 484 mm, 0.28, −0.47.
   */
  linfCm: NENUE_GROWTH.linfMm / 10,
  kPerYear: NENUE_GROWTH.k,
  t0Years: NENUE_GROWTH.t0,
  /**
   * Oʻahu's chubs' weight by fork length, least squares on the logarithms (`NENUE_SURVEY`, 443 weighed). Checks: the
   * Nagasaki chub's "BW = 3.35 × 10^-5 FL^2.95" (Ogino et al. 2020, p. E) 8.5% heavier at 20 cm and 3.8% at 30; the blue
   * sea chub's "W = 0.0294(FL)2.94" (*K. cinerascens*, Papua New Guinea; Longenecker et al. 2017, *Acta Ichthyol.
   * Piscat.* 47: 107, Table 1, handed over by the user) 3.6% heavier at 20 cm, 1.2% lighter at 30 (MEASURED).
   */
  weightA: NENUE_SURVEY.weightA,
  weightB: NENUE_SURVEY.weightB,
  /**
   * Oʻahu's females' share mature by fork length, a logistic (`NENUE_SURVEY`, 143 females): half at 327.8 mm, between
   * Weng's species' 302 mm (the group of three), 346 (*K. cinerascens*) and 376 (*K. vaigiensis*) (Weng 2025's report,
   * p. 1), carried to age by the growth above: half at its age there, its slope the length's times the growth's rate
   * there, k (L∞ − L50). Checks: the Nagasaki chub's 3.24 years (Ogino et al. 2020, p. G); the blue sea chub's "L50 at
   * 22.6 cm FL" in Papua New Guinea (Longenecker et al. 2017, p. 115). Gonochores: "We classified K. cinerascens as a
   * gonochore" (ibid.); no sex change, as the life has none.
   */
  maturitySlope: NENUE_SURVEY.femaleMaturityPerMm * NENUE_GROWTH.k * (NENUE_GROWTH.linfMm - NENUE_SURVEY.femaleHalfMatureForkMm),
  maturityAgeYears: nenueAgeAt(NENUE_SURVEY.femaleHalfMatureForkMm),
  /** "The observed maximum age for females and males was 46 years (544 mm FL) and 32 years (463 mm FL) respectively" (p. G). */
  maxAgeYears: NENUE_OLDEST_YEARS,
  naturalPerYear: 5.4 / NENUE_OLDEST_YEARS,
  /**
   * The fork lengths Hawaiʻi's fishers keep, MRIP's interviewers' measures, 2003 to 2025 (`scripts/nenue.ts`): half taken at
   * the length at first capture, most at the mode (ESTIMATED), in total length by Oʻahu's chubs' lengths.
   */
  takenHalfTlMm: nenueTlMm(NENUE_SURVEY.takenHalfForkMm),
  takenMostTlMm: nenueTlMm(NENUE_SURVEY.takenMostForkMm),
  /**
   * NOAA's divers' chubs of the most-taken size and more, their mean (`scripts/nenue.ts`): a check, the fishing the
   * catch's. The divers see few large chubs (none over 40 cm of fork length counted, where the fishers keep them to
   * 55), or the Japanese chub's growth is not Hawaiʻi's (open; `docs/KNOWN_ISSUES.md` M178).
   */
  takenMeanTlMm: nenueTlMm(NENUE_SURVEY.takenMeanForkMm),
  /**
   * The main islands' catch a year, 2023 to 2025 (`scripts/nenue.ts`): MRIP's estimates of the chubs landed by
   * Hawaiʻi's recreational fishers, each fish the mean weight of those its interviewers measured, and DAR's commercial
   * landings; no assessment of the chubs' fishing was found (M178's reader). MRIP's estimates are poor (their PSEs 27%
   * to 102%): open.
   */
  catchKgPerYear: NENUE_SURVEY.catchKg,
  /**
   * The main islands' fish of the sizes taken, kg: Maui Nui's density of them, NOAA's divers' chubs at the four islands
   * 2013 to 2024 (`scripts/nenue.ts`), over the main islands' hard bottom, as every stock's is held: Maui Nui's own
   * density, where the others' are the main islands' (Nadon 2017's, which has no chubs).
   */
  surveyTakenKg: NENUE_SURVEY.takenKgPerHa * MAIN_ISLANDS_HARD_BOTTOM_HA,
  /** Total length cm from fork length cm, Oʻahu's chubs' least squares (`NENUE_SURVEY`, 434 measured both ways), so the game weighs a fish as NOAA's divers' lengths read. */
  ipTlInterceptCm: NENUE_SURVEY.tlInterceptMm / 10,
  ipTlSlope: NENUE_SURVEY.tlSlope,
  tpTlSlope: NENUE_SURVEY.tlSlope,
  tpTlInterceptCm: NENUE_SURVEY.tlInterceptMm / 10,
  /**
   * Wren et al.'s 45 days, "representative for most reef fish" (2016, p. 3): no chub's larval life was found. Its young
   * drift under rafts: "juvenile stages of four reef fishes ... sea chub, Kyphosus cinerascens" among a drifting raft's
   * fish off the island of Hawaiʻi (Gooding and Magnuson 1967, *Pacific Science* 21: 486, p. 489): a longer life afloat,
   * unmeasured (open).
   */
  larvalDays: 45,
  /**
   * Moʻomomi's calendar, Molokaʻi, 2000: "enenue (Kyphosus species)" spawning at peak in January and February, seen in
   * March to May (Poepoe, Bartram and Friedlander, *Putting Fishers' Knowledge to Work*, 2001, Table 1, p. 333; its
   * community's monitors "observing spawning behavior and sampling fish gonads", p. 332; the shading read off the page).
   * Oʻahu's three-species group the same, January to May, by its females' gonads; *K. vaigiensis* and *K. cinerascens*
   * "Year round" (Weng 2025's report, p. 1): Maui Nui's own calendar and the group of the most fish taken, the two
   * year-round species' other months open. Off Nagasaki the chub spawns June to October (Yamaguchi et al. 2011, as Ogino
   * et al. quote it).
   */
  spawningMonths: [1, 2, 3, 4, 5],
  /** Its young crowded over the parrotfish's three months, as its steepness gives (M214). */
  crowdedMonths: 3,
  steepness: REEF_FISH_STEEPNESS,
};

/**
 * Nadon 2017's blue goatfish (p. 136): "Linf 565 mm", "K 0.512 yr-1", "a0 -0.6 yr", "Longevity 5.4 yr", each "Mean and
 * SD: Nadon & Ault (2016)", the stepwise approach from its largest length ("There were no published life history
 * parameters for this species and the stepwise approach was used", "The Lmax used for this analysis was obtained from
 * the pristine NWHI", 494 mm): ESTIMATED, the length total. Carried to fork length by Oʻahu's fish (`MOANOKEA_SURVEY`):
 * a curve L∞ (1 − e^(−k (t − t0))) in total length is one in fork length with L∞' = (L∞ − a)/b and the same k, its t0
 * moved by ln(L∞ / (L∞ − a)) / k.
 */
const MOANOKEA_TL_GROWTH = { linfMm: 565, k: 0.512, t0: -0.6 } as const;
const MOANOKEA_FORK_LINF_MM = (MOANOKEA_TL_GROWTH.linfMm - MOANOKEA_SURVEY.tlInterceptMm) / MOANOKEA_SURVEY.tlSlope;
const MOANOKEA_T0 = MOANOKEA_TL_GROWTH.t0 + Math.log(MOANOKEA_TL_GROWTH.linfMm / (MOANOKEA_TL_GROWTH.linfMm - MOANOKEA_SURVEY.tlInterceptMm)) / MOANOKEA_TL_GROWTH.k;
/** Its oldest age, Nadon's 5.4 years to the month a class is (65 months). */
const MOANOKEA_OLDEST_YEARS = 65 / 12;
/** The age at a fork length mm by the growth above. */
const moanoKeaAgeAt = (forkMm: number): number => MOANOKEA_T0 - Math.log(1 - forkMm / MOANOKEA_FORK_LINF_MM) / MOANOKEA_TL_GROWTH.k;

/**
 * The moano kea, the blue goatfish (*Parupeneus cyclostomus*), each figure with its source as read (M179): the goatfish
 * NOAA assesses whose density, sizes and life are measured or compiled (Nadon 2017, pp. 136 to 137) and whose fish
 * were measured on Oʻahu (Weng 2025, NCEI Accession 0303929, CC0, `scripts/moanokea.ts`), the one goatfish the ulua's
 * stomachs name (Sudekum et al. 1991, Table 3): the ulua's and the ʻōmilu's prey (`predation.ts`). The manybar goatfish
 * (*P. multifasciatus*), the most counted at Maui Nui and the ʻōmilu's other named goatfish, is not assessed (open).
 */
export const MOANOKEA: FishLife = {
  id: 'moanokea',
  linfCm: MOANOKEA_FORK_LINF_MM / 10,
  kPerYear: MOANOKEA_TL_GROWTH.k,
  t0Years: MOANOKEA_T0,
  /**
   * Oʻahu's blue goatfish's weight by fork length, least squares on the logarithms (`MOANOKEA_SURVEY`). Nadon's own,
   * "L-W α 8.28e-6", "L-W β 3.13", is Kulbicki 2001's (p. 136), on total length; it weighs a fish of 30 cm fork length
   * 0.4693 kg against Oʻahu's 0.5434, 13.6% lighter (MEASURED in `tests/moanokea.test.ts`).
   */
  weightA: MOANOKEA_SURVEY.weightA,
  weightB: MOANOKEA_SURVEY.weightB,
  /**
   * Oʻahu's females' share mature by fork length, a logistic (`MOANOKEA_SURVEY`), carried to age by the growth above:
   * half at its age there, its slope the length's times the growth's rate there, k (L∞ − L50). Weng's report gives
   * "246 mm" for the females (95%: 230 to 261 mm, p. 1), the same fish fitted by its own package; Nadon's compiled
   * "Lmat 269" mm, total length (p. 136), 235 mm fork. Gonochores: no sex change, as the life has none.
   */
  maturitySlope: MOANOKEA_SURVEY.femaleMaturityPerMm * MOANOKEA_TL_GROWTH.k * (MOANOKEA_FORK_LINF_MM - MOANOKEA_SURVEY.femaleHalfMatureForkMm),
  maturityAgeYears: moanoKeaAgeAt(MOANOKEA_SURVEY.femaleHalfMatureForkMm),
  maxAgeYears: MOANOKEA_OLDEST_YEARS,
  naturalPerYear: 5.4 / MOANOKEA_OLDEST_YEARS,
  /** "LS50 225", "LS95 250" mm, "DAR commercial data" (Nadon 2017, p. 136). */
  takenHalfTlMm: 225,
  takenMostTlMm: 250,
  /**
   * "L diver survey 318" mm, 362 fish, "NOAA-CREP diver survey" (p. 136): a check, the fishing the catch's. Nadon's own
   * fishing, F 0.73, is its total deaths from this length less its natural deaths (M 0.60), all of what is not natural
   * its fishing; the game's jacks eat it besides (`predation.ts`), and its fishing from the length would count their
   * meals twice.
   */
  takenMeanTlMm: 318,
  /**
   * "Total catch 4,474" kg a year, the main islands' (commercial 700, recreational 3,684; p. 136), its fishing the catch
   * over the survey's fish of the sizes taken. Nadon: "The catch data for this species seemed unusually small (4,474 kg)
   * compared to other species with similar abundance": open.
   */
  catchKgPerYear: 4_474,
  /** "B from survey 77,428" kg, its SD 30,321 (p. 136). */
  surveyTakenKg: 77_428,
  /** Total length cm from fork length cm, Oʻahu's fish's least squares (`MOANOKEA_SURVEY`). */
  ipTlInterceptCm: MOANOKEA_SURVEY.tlInterceptMm / 10,
  ipTlSlope: MOANOKEA_SURVEY.tlSlope,
  tpTlSlope: MOANOKEA_SURVEY.tlSlope,
  tpTlInterceptCm: MOANOKEA_SURVEY.tlInterceptMm / 10,
  /** Wren et al.'s 45 days, "representative for most reef fish" (2016, p. 3): no goatfish's larval life in Hawaiʻi was read (open). */
  larvalDays: 45,
  /** "Year round", Oʻahu's females by their gonads (Weng 2025's report, p. 1). */
  spawningMonths: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
  /**
   * Its predators, the jacks, take it at every size: no length of a goatfish a jack ate was read (Sudekum et al. 1991
   * measured its prey, "the length, weight, and extent of digestion", p. 496, and reports none; M179's reader), and the
   * one blue goatfish in an ulua's stomachs was 8.2 of the goatfish's 8.6% of its food by volume (Table 3), no small
   * fish. The roi's sizes (`PREY_SIZE`) are its parrotfish's, a predator a tenth an ulua's length.
   */
  takenAtEverySize: true,
};

/**
 * The convict tang, *Acanthurus triostegus sandvicensis*, the manini (M220): a surgeonfish of the reef's shallows that
 * grazes its algae ("Fish Grazer/Detritivores", Weijerman, Fulton and Parrish 2013, Table S2, CC0), at Maui Nui
 * `REEF_SURVEYS.manini.perHa` a hectare by NOAA's divers (`scripts/reefSurveys.ts`, MEASURED). Its life is Hawaiʻi's one
 * study of it, from fish of Hawaiʻi, Maui and Oʻahu: Longenecker, Langston and Eble 2008, *Growth, mortality and
 * reproduction of manini, Acanthurus triostegus sandvicensis*, a final report to the State's Division of Aquatic
 * Resources (Hawaiʻi Biological Survey, Bishop Museum; no license stated, its facts cited). No stock assessment holds it
 * (Nadon 2017 has no manini, M220's reader). It eats no stock of the game's and no diet the game reads names it eaten: its
 * balance is its own, as the kala's (M90).
 */
export const MANINI: FishLife = {
  id: 'manini',
  /**
   * Its growth by its otoliths' daily rings: "lt = 202.798(1-e-0.0039276(t-18.6533))" (Longenecker et al. 2008, p. 2),
   * lt mm of "fork" length, from the head to the end of the tail's middle rays (p. 5), t days: K 0.0039276 a day,
   * 1.4346 a year, and t0 18.6533 days. Fitted to 45 fish, its rings not validated (p. 13).
   */
  linfCm: 20.2798,
  kPerYear: 0.0039276 * 365.25,
  t0Years: 18.6533 / 365.25,
  /** "W = 0.00001214(FL)3.1557; n = 115; r2 = 0.98" (p. 12, Figure 5), W g, FL mm: per cm, 0.00001214 · 10^3.1557. */
  weightA: 0.00001214 * 10 ** 3.1557,
  weightB: 3.1557,
  maturitySlope: Infinity,
  /** "50% of females were mature by 164 mm" (p. 14): its females' age there, at a step, as the kala's. */
  maturityAgeYears: 18.6533 / 365.25 - Math.log(1 - 164 / 202.798) / (0.0039276 * 365.25),
  /**
   * Its oldest: "manini live for more than four years" (p. 2), "a maximum life span exceeding four years" (p. 18), "likely
   * to be low" as the authors judge their own ages: five years, a year past it, JUDGED; at its measured natural deaths
   * (below) e^(−2.142 · 5) of a settling month's fish, 2.2 in a hundred thousand, are left at five.
   */
  maxAgeYears: 5,
  /**
   * Its natural deaths, MEASURED in the State's reserves: "Total quarterly mortality in marine reserves, which equals
   * natural mortality, is 0.53549" (p. 17), a quarter's: 2.142 a year.
   */
  naturalPerYear: 0.53549 * 4,
  /**
   * The sizes taken: Hawaiʻi's least, "Manini ... 6 inches" since 2024 (DAR's rules booklet, 2025; five inches before,
   * BLNR item F-2, 2023, p. 1), its length "the ... snout to the middle of the trailing edge of the tail" as
   * Longenecker's "fork" length is (M220's reader, HAR §13-95-5): knife-edged at 152.4 mm, fishers taking none
   * shorter (a choice: the rule's fish kept, its poaching not modeled).
   */
  takenHalfTlMm: 152.4,
  takenMostTlMm: 152.4 + 1,
  /** Unused: its fishing is measured (below), not fitted to a length. */
  takenMeanTlMm: 0,
  /**
   * Its fishing, MEASURED in the State's fished areas against its reserves: "Total quarterly mortality in fished areas
   * is 0.68170" (p. 17), less its natural deaths, 0.14621 a quarter (p. 5): 0.585 a year.
   */
  fishingPerYear: (0.68170 - 0.53549) * 4,
  /** Unused: its density is in animals (`densityPerHa`). */
  surveyTakenKg: 0,
  /**
   * Its total length its "fork" length, as its rule measures both alike (above): the divers' total lengths taken as it,
   * ESTIMATED (no measured relation read).
   */
  ipTlInterceptCm: 0,
  ipTlSlope: 1,
  tpTlSlope: 1,
  /** "Basch (unpubished data) showed that the presettlement phase for A. triostegus sandvicensis lasts an average of 54 d" (p. 8): compiled. */
  larvalDays: 54,
  /**
   * Its spawning: no Maui Nui season is read. Its gonads by month at Maunalua, Oʻahu, high from January to June and
   * highest in August, low in July and in October and November (Schemmel's 2015 slides to DAR, slide 32, read off its
   * figure; unpublished, M220's reader): those seven months, a choice from the one measured year round (Randall 1961,
   * unread, ScholarSpace's bot check, asked of the user).
   */
  spawningMonths: [1, 2, 3, 4, 5, 6, 8],
  /** Its young crowded over the parrotfish's three months, as its steepness gives (M214's, M220). */
  crowdedMonths: 3,
  steepness: REEF_FISH_STEEPNESS,
  /** NOAA's divers' count at Maui Nui, a hectare (`REEF_SURVEYS`, MEASURED): its balance's density (as the octopus's, M129). */
  densityPerHa: REEF_SURVEYS.manini.perHa,
};

/**
 * The bluestripe snapper, *Lutjanus kasmira*, the taʻape (M218): brought to Hawaiʻi from the Marquesas, 2,435 fish in 1958,
 * and the Society Islands, 728 in 1961 (Gaither et al. 2013, *PLoS ONE* 8: e56487, p. 1, CC BY), at Maui Nui
 * `REEF_SURVEYS.taape.perHa` a hectare by NOAA's divers (MEASURED), its table balanced to that count (`densityPerHa`,
 * M252, as the manini's, M220; before it, to Nadon's weight of the sizes taken, about 64 a hectare, a third of the
 * divers', codebase review 29's M247). Its life is NOAA's length-based assessment's (Nadon
 * 2017, NOAA TM NMFS-PIFSC-60, p. 109, public domain), its growth Oʻahu's otoliths' as Nadon takes it. It eats the reef's
 * crustaceans and fish: of 180 taken by handline, its prey's index 49.9% benthic crustaceans and 24.4% fish, one blue
 * goatfish among them (Parrish et al. 2000, a DAR report, Tables 16, 17 and 19, its facts cited), the moano kea's kind,
 * left out as one fish in 180 (JUDGED); and no diet the game reads names it eaten (the roi's 159 stomachs held none, Dierking et al. 2009, public
 * domain). So its balance is its own, as the kala's (M90), its prey the reef's the game does not hold (open).
 */
export const TAAPE: FishLife = {
  id: 'taape',
  /**
   * "Linf 340 ... mm", "K 0.29 ... yr-1 ... Mean: Morales-Nin and Ralston (1990)", "a0 -1.37 ... yr" (Nadon 2017, p. 109),
   * total length: Oʻahu's 171 fish aged by their otoliths' annual rings (Morales-Nin and Ralston 1990, *J. Fish Biol.* 36:
   * 191, its facts cited).
   */
  linfCm: 34,
  kPerYear: 0.29,
  t0Years: -1.37,
  /** "L-W α 4.30e-6", "L-W β 3.25", "Kulbicki (2001)" (Nadon 2017, p. 109), g and mm of total length: per cm, 4.30e-6 · 10^3.25. */
  weightA: 4.3e-6 * 10 ** 3.25,
  weightB: 3.25,
  maturitySlope: Infinity,
  /** "Lmat 200 ... mm ... Mean: Allen (1985)" (Nadon 2017, p. 109), a field guide's, compiled ("the maturity parameter source was not ideal (i.e. a fish ID guide)", ibid.): its age there, at a step, as the kala's. */
  maturityAgeYears: -1.37 - Math.log(1 - 20 / 34) / 0.29,
  /**
   * "Longevity 8 ... yr ... Mean: Loubens (1980)" (Nadon 2017, p. 109), New Caledonia's, which Nadon doubts ("snapper
   * typically live longer (16 years)", ibid.); Oʻahu's oldest read 6 (Morales-Nin and Ralston 1990, Table I).
   */
  maxAgeYears: 8,
  /** "M 0.39 ... yr-1" (Nadon 2017, p. 109), from that longevity: Nadon's, taken as the assessment's. */
  naturalPerYear: 0.39,
  /** "LS50 220 ... LS95 240 ... mm ... DAR commercial data" (Nadon 2017, p. 109), fitted to the State's commercial sizes. */
  takenHalfTlMm: 220,
  takenMostTlMm: 240,
  /** "L diver survey 274 4 mm 1402 NOAA-CREP diver survey" (Nadon 2017, p. 109), MEASURED: the length Nadon's fishing is fitted to. */
  takenMeanTlMm: 274,
  /** "F 0.21 0.13 yr-1" (Nadon 2017, p. 109), fitted to that length ("a population near equilibrium", ibid.). */
  fishingPerYear: 0.21,
  /** "B from survey 496,235 98,182 kg" (Nadon 2017, p. 109): the main islands' fish of the sizes taken, its divers'; its survey's error's base (`monitoring.ts`), its balance held to the count below. */
  surveyTakenKg: 496_235,
  /** Every length here total length (Nadon's divers' and the growth's alike, M218's reader): none taken from another. */
  ipTlInterceptCm: 0,
  ipTlSlope: 1,
  tpTlSlope: 1,
  /** No larval duration of the taʻape or any Indo-Pacific *Lutjanus* was read (M218's reader): Wren et al.'s model's 45 days (`larvalDays`'s note). */
  larvalDays: 45,
  /**
   * Its spawning: Oʻahu's females' share mature by month, sampled March to October alone, "about 12%" in March and April,
   * 26% in May, 52% in July, 73% in August and about 50% in September and October (Morales-Nin and Ralston 1990, Fig. 2,
   * p. 195, read off the figure by M218's reader): the months half of them or more were mature, a choice (November to
   * February not sampled, open).
   */
  spawningMonths: [7, 8, 9, 10],
  /** Its young crowded over the parrotfish's three months, as its steepness gives (M214's, M218). */
  crowdedMonths: 3,
  steepness: REEF_FISH_STEEPNESS,
  /** NOAA's divers' count at Maui Nui, a hectare, its fish of 6 to 35 cm (`REEF_SURVEYS`, MEASURED): its balance's density (M252, as the manini's). */
  densityPerHa: REEF_SURVEYS.taape.perHa,
};

/** Every reef fish the game has, in the order a sanctuary holds them (the octopus among them since M129: one model of a stock by age; the nenue since M178; the moano kea since M179; the manini since M220; the taʻape since M218). */
export const FISH_LIVES = [BULLETHEAD, PALENOSE, ROI, KALA, ULUA, OMILU, REDLIP, OCTOPUS, NENUE, MOANOKEA, MANINI, TAAPE] as const;
export type FishSpeciesId = FishLife['id'];

/** Maui Nui's hard bottom to 30 m, km²: "Reef area is to 30-m depth and excludes soft bottom habitat" (Nadon 2017, Table 2), the area NOAA's density is reckoned over. */
export const MAUI_NUI_HARD_BOTTOM_KM2 = 269;
/**
 * Each island's hard bottom to 30 m, m², `REEF_ISLANDS` order: Maui Nui's
 * 269 km² shared out by each island's reef floor from the shore to 30 m
 * (`MAUI_NUI_REEF`, GEBCO's), every island's floor taken to be as much hard
 * bottom as Maui Nui's is on the whole (M84: the map that would say, NOAA
 * NCCOS's, may not be carried, M45). The area a young fish's crowding is
 * reckoned over.
 */
export const ISLAND_HARD_BOTTOM_M2: readonly number[] = (() => {
  const floors = stratumFloorKm2(MAUI_NUI_REEF);
  const island = REEF_ISLANDS.map((_, i) => DEPTH_BINS.reduce((a, _b, k) => a + (floors[i * DEPTH_BINS.length + k] as number), 0));
  const all = island.reduce((a, b) => a + b, 0);
  return island.map((km2) => (km2 / all) * MAUI_NUI_HARD_BOTTOM_KM2 * 1e6);
})();

/** A month's age class for each month since settling to its oldest age, then one more for every fish older. */
export const ageClasses = (life: FishLife): number => 12 * life.maxAgeYears + 1;
/** Maui Nui's four islands, the stocks' order (`REEF_ISLANDS`). */
export const FISH_ISLANDS = REEF_ISLANDS;
/** The exchange's sources: the four islands, then the rest of the main islands (Niʻihau, Kauaʻi, Oʻahu and Hawaiʻi). */
export const FISH_SOURCES = [...REEF_ISLANDS, 'rest'] as const;

const logistic = (slope: number, mid: number, x: number): number => (slope === Infinity ? (x >= mid ? 1 : 0) : 1 / (1 + Math.exp(-slope * (x - mid))));

/**
 * The steps a month's days are counted in, for the classes' ages (a
 * definition: a day's share of its month, to a thirtieth).
 */
const MONTH_STEPS = 30;
/** The day's step: how far through its month it is, in thirtieths (1 to 30). */
export function monthStep(epochDay: number): number {
  const { year, month, day } = civilFromEpochDay(epochDay);
  return Math.round((MONTH_STEPS * day) / daysInMonth(year, month));
}
/**
 * A class's fish's mean age in years, from hatching, on a day `step`
 * thirtieths through the month: the fish settled evenly over their month, so
 * a class a month on is half a month younger than its number at the month's
 * start and half a month older at its end, and this month's settlers half
 * the month so far; their days afloat before. A class's age runs on through
 * the month and on into the next class's on the first, so nothing a class
 * weighs jumps when the classes age.
 */
export function classAgeYears(life: FishLife, cls: number, step: number): number {
  const months = cls === 0 ? step / (2 * MONTH_STEPS) : cls - 0.5 + step / MONTH_STEPS;
  return months / 12 + life.larvalDays / YEAR_DAYS;
}
/** Fork length cm at an age in years. */
export const forkLengthCm = (life: FishLife, ageYears: number): number => life.linfCm * (1 - Math.exp(-life.kPerYear * (ageYears - life.t0Years)));
/** Weight kg at a fork length cm: the initial phase's curve (both phases' where one is fitted to both). */
export const weightKg = (life: FishLife, forkCm: number): number => (life.weightA * forkCm ** life.weightB) / 1000;
/** The share of initial-phase fish mature at an age (the females' curve, taken for the phase's males too). */
export const matureShare = (life: FishLife, ageYears: number): number => logistic(life.maturitySlope, life.maturityAgeYears, ageYears);
/**
 * The share of all fish terminal-phase males at an age: the fitted share
 * (`sexChangeSlope`), taken from the mature, as a fish changes sex once
 * mature. The bullethead's fit alone runs on below the ages aged, and would
 * count 3.0% of the fish settled half a month as terminal-phase males
 * (MEASURED, M74, at 0.144 years); its share of the mature, the same share of
 * every fish that has matured, comes to 47.3% of all fish at its median age
 * where the fit gives half, and to 82.2% at six years where it gives 82.3%
 * (the mature then 99.85%).
 */
export const terminalShare = (life: FishLife, ageYears: number): number => (life.sexChangeSlope === undefined || life.sexChangeAgeYears === undefined ? 0
  : (life.sexChangeShare ?? 1) * logistic(life.sexChangeSlope, life.sexChangeAgeYears, ageYears) * matureShare(life, ageYears));
/** The share the fishery takes at a total length mm (Nadon 2017, eq. 1). */
export const takenShare = (life: FishLife, tlMm: number): number => 1 / (1 + Math.exp(-Math.log(19) * (tlMm - life.takenHalfTlMm) / (life.takenMostTlMm - life.takenHalfTlMm)));

/** Each age class's figures, worked out once: its weight, its total length, its shares young, adult and terminal, the share taken, and its spawners' weight a fish. */
export interface AgeClass {
  weightKg: number;
  /** The weight of its mature fish a fish of the class, each phase on its own curve (review M82: a class's mean weight, its young among it, understated the palenose's spawners by 0.13%). */
  spawnerKg: number;
  young: number;
  adult: number;
  terminal: number;
  tlMm: number;
  taken: number;
}
function ageClass(life: FishLife, age: number): AgeClass {
  const byAge = life.weightAtAgeDays;
  if (byAge) {
    // Weighed by its age, no length or phases: the mature its spawners, and the fishery's by its least weight.
    const kg = byAge.aKg * (age * YEAR_DAYS) ** byAge.b;
    const mature = matureShare(life, age);
    return { weightKg: kg, spawnerKg: mature * kg, young: 1 - mature, adult: mature, terminal: 0, tlMm: 0, taken: kg >= (life.takenMinKg ?? Infinity) ? 1 : 0 };
  }
  const fork = forkLengthCm(life, age);
  const terminal = terminalShare(life, age);
  const mature = matureShare(life, age);
  // Every fish once: the young immature, the adults mature and in the initial phase, the rest terminal-phase males.
  // Total length by phase, each phase's own conversion; weight by phase where each phase has its own curve. Never
  // shorter than the fork length (M128): the lines are fitted to grown fish, and below their sizes the parrotfish's
  // fall under it (MEASURED over every class's age at every step: the redlip's about 21 mm short at a fork length of
  // 5 cm, 28 mm at its smallest; the palenose's at most about 4.6 mm, the bullethead's 2.1 mm), which no fish's tail can
  // be; the predators' sizes start at 5 cm (`PREY_SIZE`).
  const tlMm = Math.max(10 * fork, 10 * ((1 - terminal) * (life.ipTlInterceptCm + life.ipTlSlope * fork) + terminal * life.tpTlSlope * fork + terminal * (life.tpTlInterceptCm ?? 0)));
  const tp = life.tpWeight;
  const ipKg = weightKg(life, fork);
  const tpKg = tp ? (tp.a * fork ** tp.b) / 1000 : ipKg;
  return {
    weightKg: tp ? (1 - terminal) * ipKg + (terminal * tp.a * fork ** tp.b) / 1000 : ipKg,
    spawnerKg: (mature - terminal) * ipKg + terminal * tpKg,
    tlMm,
    young: 1 - mature,
    adult: mature - terminal,
    terminal,
    taken: takenShare(life, tlMm),
  };
}
/** Each step's classes (`MONTH_STEPS` + 1 of them, from the month's start), for a life, worked out once. */
const classesByLife = new Map<FishLife, readonly (readonly AgeClass[])[]>();
export function classesOf(life: FishLife): readonly (readonly AgeClass[])[] {
  let classes = classesByLife.get(life);
  if (!classes) {
    classes = Array.from({ length: MONTH_STEPS + 1 }, (_, step) => Array.from({ length: ageClasses(life) }, (__, cls) => ageClass(life, classAgeYears(life, cls, step))));
    classesByLife.set(life, classes);
  }
  return classes;
}

/**
 * Each step's classes' weights, kg (`classesOf`'s `weightKg`), for a life, one array of numbers a step, worked out once:
 * a sum over a stock's every class each day reads them so, not class by class (MEASURED, review M190's simplifier: the
 * herbivores' weight, `herbivoreRatios`, beside each of a seed-42 world's 3,650 days, 70.4 to 74.9 µs a day through
 * the classes and 13.6 to 13.7 through these, three runs of each in turn, the same ratios to the bit).
 */
const weightsByLife = new Map<FishLife, readonly Float64Array[]>();
export function classWeightsKg(life: FishLife): readonly Float64Array[] {
  let weights = weightsByLife.get(life);
  if (!weights) {
    weights = classesOf(life).map((step) => Float64Array.from(step, (k) => k.weightKg));
    weightsByLife.set(life, weights);
  }
  return weights;
}

/**
 * A stock's fishing on an island (M170): its rate a year on the sizes taken, and, where the size rules there are not the
 * ones its rate was measured under, each step's classes' shares taken under them (`management.ts`'s `takenUnder`; the
 * classes' own `taken` otherwise). A table is made once for each rule, so one rule is one table.
 */
export interface IslandFishing {
  perYear: number;
  taken?: readonly (readonly number[])[];
}

/** A stock's fishing where nothing but its measured rate holds. */
export const stockFishing = (supply: FishSupply): IslandFishing => ({ perYear: supply.fishingPerYear });

/**
 * Each step's classes' chances of dying in a day, at a fishing rate and its shares taken: natural deaths and fishing on
 * the share taken, the yearly rates over `YEAR_DAYS`. Worked out once for each life, table and rate in use (the game's,
 * since M165 a protected island's and since M170 each island's under its rules; review M76: one kept at a time, not
 * every rate the balance tried, about 800, so the last few are kept).
 */
const deathsAt = new Map<FishLife, Map<readonly (readonly number[])[] | null, Map<number, readonly (readonly number[])[]>>>();
const RATES_KEPT = 8;
function dailyDeaths(life: FishLife, fishing: IslandFishing): readonly (readonly number[])[] {
  let byTable = deathsAt.get(life);
  if (!byTable) deathsAt.set(life, (byTable = new Map()));
  const table = fishing.taken ?? null;
  let byRate = byTable.get(table);
  if (!byRate) byTable.set(table, (byRate = new Map()));
  let deaths = byRate.get(fishing.perYear);
  if (!deaths) {
    // An animal that dies at its oldest age dies there, none carried on in the last class (`diesAtOldest`).
    const last = ageClasses(life) - 1;
    const f = fishing.perYear;
    deaths = classesOf(life).map((step, s) => step.map((k, c) => (life.diesAtOldest && c === last ? 1
      : 1 - Math.exp(-(life.naturalPerYear + f * (table ? (table[s]?.[c] as number) : k.taken)) / YEAR_DAYS))));
    if (byRate.size >= RATES_KEPT) byRate.delete(byRate.keys().next().value as number);
    byRate.set(f, deaths);
  }
  return deaths;
}

/** A class's share taken on a day `step` thirtieths through its month, under an island's fishing. */
const takenAt = (life: FishLife, fishing: IslandFishing, step: number, cls: number): number =>
  (fishing.taken ? fishing.taken[step]?.[cls] : classesOf(life)[step]?.[cls]?.taken) as number;

/**
 * How much more likely a newly settled fish is to die in a day for each more of its kind a square meter, m² a fish a
 * day: its steepness's fit where it has one (M214), else the wrasse's, measured (M84).
 */
export const crowdingOf = (life: FishLife): number => life.crowdingTried ?? (life.steepness === undefined ? CROWDING_M2_PER_FISH_DAY : STEEPNESS_CROWDING[life.id] ?? 0);

/**
 * A life's crowding as a day reads it: its crowded months (`crowdedMonths`), its rate (`crowdingOf`), and whether each
 * month's class is crowded by its own number alone (its steepness's, M214). Read from the life once a day, not class by
 * class: the lives are objects of many shapes, so each read of one of their fields is slow, and read in the classes'
 * loops (as `spawnersKg`'s curve and `liveFish`'s `diesAtOldest` were) they cost the tick about an eighth of itself
 * (MEASURED, review 29's simplifier: a seed-42 world's 3,650 days after its first year, eight runs of each in turn, a
 * median 896 µs a tick read in the loops, 818 to 936, against 778 read once a day, 731 to 867, each run the quicker).
 */
interface Crowding { months: number; rate: number; byClass: boolean }
const crowdingFor = (life: FishLife): Crowding => ({ months: life.crowdedMonths ?? 0, rate: crowdingOf(life), byClass: life.steepness !== undefined });

/** The young's crowding on an island as the day begins (M84): each one's chance of dying a day the more for each of them a square meter of the island's hard bottom. */
function islandCrowding(crowd: Crowding, counts: readonly number[], island: number): number {
  let young = 0;
  for (let c = 0; c < crowd.months; c++) young += counts[c] as number;
  return (crowd.rate * young) / (ISLAND_HARD_BOTTOM_M2[island] as number);
}

/**
 * A young class's crowding on an island as the day begins: the young of the crowded months together (M84's,
 * `islandCrowding`, `pooled` its figure that day), or, for a stock crowded by its steepness, its own month's class alone
 * (M214, as the wrasse's was measured, each pulse's survivors 90 days on against that pulse's settlers); none past the
 * crowded months.
 */
function classCrowding(crowd: Crowding, n: number, cls: number, island: number, pooled: number): number {
  if (cls >= crowd.months) return 0;
  return crowd.byClass ? (crowd.rate * n) / (ISLAND_HARD_BOTTOM_M2[island] as number) : pooled;
}

/** A class's chance of dying in a day: its natural and fishing deaths', and its rates of being eaten and crowded, `h` a day, in the one draw. */
function deathChance(base: number, h: number): number {
  return h > 0 ? 1 - (1 - base) * Math.exp(-h) : base;
}

/**
 * One fish's day as its class's (M148, a tagged fish's): its chance of dying, the stock's own for its class on its
 * island, and the rates a year its dead are shared out by (as `tallyDead` shares the stock's: natural, fished, eaten,
 * crowded), its oldest dead of its age where every one dies there. `counts` its island's classes as the day begins (aged),
 * `eatenPerDay` its class's chance of being eaten there (`eatenBy`'s), none where nothing eats it.
 */
export function classDay(life: FishLife, fishing: IslandFishing, counts: readonly number[], island: number, cls: number, epochDay: number, eatenPerDay: number):
  { chance: number; old: boolean; rates: { natural: number; fished: number; eaten: number; crowded: number } } {
  const step = monthStep(epochDay);
  const base = dailyDeaths(life, fishing)[step]?.[cls] as number;
  const crowd = crowdingFor(life);
  const crowdedH = classCrowding(crowd, counts[cls] as number, cls, island, islandCrowding(crowd, counts, island));
  const old = life.diesAtOldest === true && cls === ageClasses(life) - 1;
  const taken = takenAt(life, fishing, step, cls);
  return {
    chance: deathChance(base, eatenPerDay + crowdedH),
    old,
    rates: old ? { natural: 1, fished: 0, eaten: 0, crowded: 0 } : { natural: life.naturalPerYear, fished: fishing.perYear * taken, eaten: eatenPerDay * YEAR_DAYS, crowded: crowdedH * YEAR_DAYS },
  };
}

/** The draws a day's step takes: the game's from each island's stream, the spin-up's their expected values. */
export interface FishDraws {
  /** How many of n die on an island (`FISH_ISLANDS` order), each with chance p. */
  deaths(n: number, p: number, island: number): number;
  /** How many settle on an island, of an expected number. */
  settlers(mean: number, island: number): number;
}
/** The expected values, for the spin-up and for opening a world (no draw). */
export const EXPECTED: FishDraws = { deaths: (n, p) => n * p, settlers: (mean) => mean };

/** A stock on the balance's first of January (`data/fish/`): each island's fish, and the larvae afloat by their days to settle from that day (`afloatAhead`). */
export interface FishJanuary {
  islands: readonly (readonly number[])[];
  afloat: readonly (readonly number[])[];
}

/** What the exchange and the balance give (`data/fish/`). */
export interface FishSupply {
  /**
   * By release month (January first), from each source (`FISH_SOURCES`) to
   * each island (`FISH_ISLANDS`): the chance a larva settles there, as
   * Wren et al.'s model measures it.
   */
  exchange: readonly (readonly (readonly number[])[])[];
  /** Larvae a kilogram of spawners sends out each spawning day that settle and live to be counted, set by the balance. */
  larvaePerKgDay: number;
  /** The rest of the main islands' spawners, kg, held. */
  restSpawnersKg: number;
  /** Fishing deaths a year on the sizes taken, set by the balance so the fish fully taken average NOAA's measured length (`takenMeanTlMm`), or from the catch (`catchKgPerYear`). */
  fishingPerYear: number;
  /** A predator's other prey on each island (`FISH_ISLANDS` order), kg weighed as its prey stocks' are (`predation.ts`, M84), set by the balance. */
  otherPreyKg?: readonly number[];
  /**
   * A prey's: each predator's other prey on each island in its hunt on it alone (`PredatorDiet.otherPreyIn`; the ulua's
   * against the octopus, M129; each of its two predators' against the blue goatfish, M179), set by the balance.
   */
  predatorOtherPreyKg?: Readonly<Partial<Record<FishSpeciesId, readonly number[]>>>;
}

/**
 * An island's day for the death audit (`audit.ts`, M80): its dead by cause,
 * in fish and in kg at their class's weight, and its settlers. A class's
 * dead, one draw at the sum of its natural deaths, its fishing, its
 * predation and its crowding (M84), are shared out by each one's part of
 * that sum.
 */
export interface IslandDay {
  /** `FISH_ISLANDS` order. */
  island: number;
  natural: number;
  fished: number;
  /** Eaten by the predators the stock's day names (M84; several since M126). */
  predation: number;
  /** Died of the young's crowding (M84). */
  crowding: number;
  naturalKg: number;
  fishedKg: number;
  predationKg: number;
  crowdingKg: number;
  settled: number;
}

/** A class's dead, shared out by cause onto an island's day: `fishingPerYear` its fishing on it (its rate times its share taken), `eatenPerDay` its chance of being eaten and `crowdedPerDay` its crowding's, rates a day. */
function tallyDead(life: FishLife, t: IslandDay, k: AgeClass, dead: number, fishingPerYear: number, eatenPerDay: number, crowdedPerDay: number): void {
  const fishing = fishingPerYear;
  const eatenPerYear = eatenPerDay * YEAR_DAYS;
  const crowdedPerYear = crowdedPerDay * YEAR_DAYS;
  const all = life.naturalPerYear + fishing + eatenPerYear + crowdedPerYear;
  const fished = (dead * fishing) / all;
  const eaten = (dead * eatenPerYear) / all;
  const crowded = (dead * crowdedPerYear) / all;
  const natural = dead - fished - eaten - crowded;
  t.fished += fished;
  t.predation += eaten;
  t.crowding += crowded;
  t.natural += natural;
  t.fishedKg += fished * k.weightKg;
  t.predationKg += eaten * k.weightKg;
  t.crowdingKg += crowded * k.weightKg;
  t.naturalKg += natural * k.weightKg;
}

/** An island's spawners, kg, on a day `step` thirtieths through its month: the weight of its mature fish (Nadon 2017's measure of spawning). */
export function spawnersKg(life: FishLife, counts: readonly number[], step: number): number {
  const classes = classesOf(life)[step] as readonly AgeClass[];
  const phased = life.tpWeight !== undefined;
  let kg = 0;
  for (let c = 0; c < counts.length; c++) {
    const k = classes[c] as AgeClass;
    // One curve for both phases (the bullethead): its mature share of its weight, as M74 reckoned it, to the bit.
    kg += phased ? (counts[c] as number) * k.spawnerKg : (counts[c] as number) * (k.adult + k.terminal) * k.weightKg;
  }
  return kg;
}

/** An island's fish of the sizes the fishery takes, kg, on a day `step` thirtieths through its month, each fish weighed by the share taken at its size (as Nadon 2017 counts the surveys' fish). */
export function takenKg(life: FishLife, counts: readonly number[], step: number): number {
  const classes = classesOf(life)[step] as readonly AgeClass[];
  let kg = 0;
  for (let c = 0; c < counts.length; c++) {
    const k = classes[c] as AgeClass;
    kg += (counts[c] as number) * k.taken * k.weightKg;
  }
  return kg;
}

/**
 * The day's step, in place: on a month's first day every class a month
 * older (`ageFish`), then the day's lives (`liveFish`).
 */
export function stepFish(life: FishLife, fish: FishState, epochDay: number, supply: FishSupply, draws: FishDraws, report?: (day: IslandDay) => void): void {
  ageFish(life, fish, epochDay);
  liveFish(life, fish, epochDay, supply, draws, report);
}

/** On a month's first day, every class a month older, the oldest gathering every fish past its oldest age; on any other, nothing. */
export function ageFish(life: FishLife, fish: FishState, epochDay: number): void {
  if (civilFromEpochDay(epochDay).day !== 1) return;
  const oldest = ageClasses(life) - 1;
  for (const counts of fish.islands) {
    counts[oldest] = (counts[oldest] as number) + (counts[oldest - 1] as number);
    for (let c = oldest - 1; c > 0; c--) counts[c] = counts[c - 1] as number;
    counts[0] = 0;
  }
}

/**
 * The day's lives, the classes aged (`ageFish`): the day's deaths; the
 * larvae released `larvalDays` ago settling; and, in the spawning months,
 * the day's larvae released, to settle `larvalDays` on. The larvae afloat are
 * kept by day, in a ring of `larvalDays` slots read and filled at the same
 * slot. Given `report`, each island's day is told to it (the death audit's,
 * M80); without, nothing is tallied. Given `eaten` (M84, `predation.ts`), each
 * island's classes' chance of being eaten, a rate a day, is added to their
 * deaths, one draw with the rest; a class with none dies as it did, to the
 * bit. A life's young (`crowdedMonths`, M84) die the more the more of them
 * there are: each one's chance a day `crowdingOf(life)` times their
 * number a square meter of the island's hard bottom (`ISLAND_HARD_BOTTOM_M2`)
 * as the day begins, in the same draw; a stock crowded by its steepness
 * (M214) by its own month's class's number alone.
 */
export function liveFish(life: FishLife, fish: FishState, epochDay: number, supply: FishSupply, draws: FishDraws, report?: (day: IslandDay) => void,
  eaten?: readonly (readonly number[])[], fishing?: readonly IslandFishing[]): void {
  const { month } = civilFromEpochDay(epochDay);
  const step = monthStep(epochDay);
  const classes = classesOf(life)[step] as readonly AgeClass[];
  const oldest = ageClasses(life) - 1;
  const slot = epochDay % life.larvalDays;
  const crowd = crowdingFor(life);
  const diesAtOldest = life.diesAtOldest === true;
  for (let i = 0; i < fish.islands.length; i++) {
    // Each island's fishing (M165: lower where the sanctuary protects the stock; M170: under its size and bag limits), the stock's where none is given.
    const fishingHere = fishing?.[i] ?? stockFishing(supply);
    const deaths = dailyDeaths(life, fishingHere)[step] as readonly number[];
    const counts = fish.islands[i] as number[];
    const tally: IslandDay | null = report ? { island: i, natural: 0, fished: 0, predation: 0, crowding: 0, naturalKg: 0, fishedKg: 0, predationKg: 0, crowdingKg: 0, settled: 0 } : null;
    const eatenHere = eaten?.[i];
    const crowding = islandCrowding(crowd, counts, i);
    for (let c = 0; c <= oldest; c++) {
      const n = counts[c] as number;
      if (n > 0) {
        const eatenH = eatenHere ? (eatenHere[c] as number) : 0;
        const crowdedH = classCrowding(crowd, n, c, i, crowding);
        const p = deathChance(deaths[c] as number, eatenH + crowdedH);
        const dead = draws.deaths(n, p, i);
        counts[c] = n - dead;
        // Its oldest dead of their age where every one dies there (`diesAtOldest`), none of them fished or eaten.
        const old = diesAtOldest && c === oldest;
        if (tally !== null && dead > 0) tallyDead(life, tally, classes[c] as AgeClass, dead, old ? 0 : fishingHere.perYear * takenAt(life, fishingHere, step, c), old ? 0 : eatenH, old ? 0 : crowdedH);
      }
    }
    const afloat = fish.afloat[slot] as number[];
    const settled = draws.settlers(afloat[i] as number, i);
    counts[0] = (counts[0] as number) + settled;
    if (tally !== null && report) {
      tally.settled = settled;
      report(tally);
    }
  }
  const released = fish.afloat[slot] as number[];
  released.fill(0);
  if (!life.spawningMonths.includes(month)) return;
  const from = supply.exchange[month - 1] as readonly (readonly number[])[];
  const spawners = [...fish.islands.map((c) => spawnersKg(life, c, step)), supply.restSpawnersKg];
  for (let s = 0; s < spawners.length; s++) {
    const larvae = supply.larvaePerKgDay * (spawners[s] as number);
    const to = from[s] as readonly number[];
    for (let i = 0; i < released.length; i++) released[i] = (released[i] as number) + larvae * (to[i] as number);
  }
  // The larvae afloat reach the save: held, as every stored float is, so an engine's last bit cannot move them (review M76).
  for (let i = 0; i < released.length; i++) released[i] = held(released[i] as number);
}

/** An island's fish fully taken (total length `takenMostTlMm` and up) on a day `step` thirtieths through its month: their number and the sum of their lengths, mm, as the surveys' mean length counts them (the balance counts every day's, `takenMeanTlMm`). */
export function fullyTaken(life: FishLife, counts: readonly number[], step: number): { fish: number; lengthMm: number } {
  const classes = classesOf(life)[step] as readonly AgeClass[];
  let fish = 0;
  let lengthMm = 0;
  for (let c = 0; c < counts.length; c++) {
    const k = classes[c] as AgeClass;
    if (k.tlMm < life.takenMostTlMm) continue;
    fish += counts[c] as number;
    lengthMm += (counts[c] as number) * k.tlMm;
  }
  return { fish, lengthMm };
}

/**
 * The larvae afloat by the days until they settle, from a day on: the first
 * row settles that day, the next the day after, to the `larvalDays`th. The
 * ring's slots are the release day's mod `larvalDays`, so a stock's larvae
 * carried to another calendar day (a balance's year begun again, a world
 * opened from the balance's first of January) go by their days to settle.
 */
export function afloatAhead(life: FishLife, fish: FishState, fromDay: number): number[][] {
  return Array.from({ length: life.larvalDays }, (_, k) => [...(fish.afloat[(fromDay + k) % life.larvalDays] as number[])]);
}
/** The ring that holds larvae afloat by their days to settle (`afloatAhead`) from a day on. */
export function afloatFrom(life: FishLife, ahead: readonly (readonly number[])[], fromDay: number): number[][] {
  const ring: number[][] = Array.from({ length: life.larvalDays }, () => []);
  ahead.forEach((row, k) => { ring[(fromDay + k) % life.larvalDays] = [...row]; });
  return ring;
}

/** A stock with no fish and no larvae afloat. */
export function emptyFish(life: FishLife): FishState {
  return {
    islands: FISH_ISLANDS.map(() => Array<number>(ageClasses(life)).fill(0)),
    afloat: Array.from({ length: life.larvalDays }, () => Array<number>(FISH_ISLANDS.length).fill(0)),
  };
}

/**
 * What the card reads on a day: the young, the adults and the terminal-phase
 * males, each island's fish and their weight, kg; and each group's mean total
 * length, mm, by number (M75: the sprites drawn to it), 0 for a group with no
 * fish.
 */
export interface FishCounts {
  young: number;
  adults: number;
  terminal: number;
  kg: number;
  byIsland: number[];
  lengthMm: { young: number; adults: number; terminal: number };
}
export function fishCounts(life: FishLife, fish: FishState, epochDay: number): FishCounts {
  const classes = classesOf(life)[monthStep(epochDay)] as readonly AgeClass[];
  const out: FishCounts = { young: 0, adults: 0, terminal: 0, kg: 0, byIsland: [], lengthMm: { young: 0, adults: 0, terminal: 0 } };
  const mm = { young: 0, adults: 0, terminal: 0 };
  for (const counts of fish.islands) {
    let all = 0;
    for (let c = 0; c < counts.length; c++) {
      const n = counts[c] as number;
      const k = classes[c] as AgeClass;
      out.young += n * k.young;
      out.adults += n * k.adult;
      out.terminal += n * k.terminal;
      out.kg += n * k.weightKg;
      mm.young += n * k.young * k.tlMm;
      mm.adults += n * k.adult * k.tlMm;
      mm.terminal += n * k.terminal * k.tlMm;
      all += n;
    }
    out.byIsland.push(all);
  }
  for (const g of ['young', 'adults', 'terminal'] as const) out.lengthMm[g] = out[g] > 0 ? mm[g] / out[g] : 0;
  return out;
}
